import { Fragment, useEffect, useRef, useState } from 'react';
import { collectPhraseCards, collectDictionaryBaseTexts } from '../data/phraseCards';
import { getPhraseForLevel, getPhotoForLevel, getActiveLevels, getWordLevels, LEVEL_ORDER, buildLevels, applyWordLevel } from '../data/wordLevels';

const WORDS_KEY = '__words';
const ORDER_KEY = '__order';
const PATH_SEPARATOR = '\u001F';
const MAX_PHOTO_DATA_URL_LENGTH = 450000;
const MAX_PHOTO_DIMENSION = 500;
const isTouchDevice = typeof window !== 'undefined' && ('ontouchstart' in window || (navigator.maxTouchPoints && navigator.maxTouchPoints > 0));

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function loadImage(dataUrl) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = dataUrl;
  });
}

async function createCompactPhoto(file) {
  const originalDataUrl = await readFileAsDataUrl(file);
  const canCompress = /^image\/(jpe?g|png|webp)$/i.test(file.type || '');
  let dataUrl = originalDataUrl;
  let type = file.type || 'image/heic';

  if (canCompress) {
    try {
      const image = await loadImage(originalDataUrl);
      const scale = Math.min(1, MAX_PHOTO_DIMENSION / Math.max(image.width, image.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(image.width * scale));
      canvas.height = Math.max(1, Math.round(image.height * scale));

      const context = canvas.getContext('2d');
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      const compactDataUrl = canvas.toDataURL('image/jpeg', 0.6);

      if (compactDataUrl && compactDataUrl.length < originalDataUrl.length) {
        dataUrl = compactDataUrl;
        type = 'image/jpeg';
      }
    } catch {
      dataUrl = originalDataUrl;
    }
  }

  if (dataUrl.length > MAX_PHOTO_DATA_URL_LENGTH) {
    throw new Error('photo-too-large');
  }

  return {
    name: file.name,
    type,
    dataUrl,
  };
}

function pathToArray(path) {
  return path ? path.split(PATH_SEPARATOR) : [];
}

function arrayToPath(path) {
  return path.join(PATH_SEPARATOR);
}

function normalizeNode(node) {
  if (Array.isArray(node)) return { [WORDS_KEY]: node };
  return { ...(node || {}) };
}

function getNodeAtPath(categoryNode, path) {
  return pathToArray(path).reduce((node, part) => {
    const normalized = normalizeNode(node);
    return normalized[part];
  }, categoryNode);
}

function getWordsFromNode(node) {
  return Array.isArray(node) ? node : node?.[WORDS_KEY] || [];
}

function getSubcategoryNames(node) {
  const norm = normalizeNode(node);
  const keys = Object.keys(norm).filter((key) => key !== WORDS_KEY && key !== ORDER_KEY);
  const order = Array.isArray(norm[ORDER_KEY]) ? norm[ORDER_KEY] : null;
  if (!order) return keys;
  const present = new Set(keys);
  const ordered = [];
  order.forEach((name) => { if (present.has(name)) { ordered.push(name); present.delete(name); } });
  keys.forEach((name) => { if (present.has(name)) ordered.push(name); });
  return ordered;
}

function collectSubcategoryPaths(node, path = []) {
  return getSubcategoryNames(node).flatMap((subcategory) => {
    const childPath = [...path, subcategory];
    const childNode = normalizeNode(node)[subcategory];

    return [
      arrayToPath(childPath),
      ...collectSubcategoryPaths(childNode, childPath),
    ];
  });
}

function getNounTooltipRows(word) {
  const forms = word.manualForms || word.forms;
  if (!forms) return null;

  return [
    { label: 'ubest. ent.', value: word.text },
    { label: 'best. ent.', value: forms.singularDefinite },
    { label: 'ubest. fl.', value: forms.pluralIndefinite },
    { label: 'best. fl.', value: forms.pluralDefinite },
  ].filter((row) => row.value);
}

function getVerbTooltipRows(word) {
  const forms = word.manualForms || word.forms;
  if (!forms) return null;

  return [
    { label: 'Infinitiv', value: formatInfinitiveText(forms.infinitive || word.text) },
    { label: 'Presens', value: forms.present },
    { label: 'Preteritum', value: forms.past },
    { label: 'Perfektum', value: forms.perfect ? `har ${forms.perfect}` : '' },
    { label: 'Imperativ', value: forms.imperative },
  ].filter((row) => row.value);
}

function getAdjectiveTooltipRows(word) {
  const forms = word.manualForms || word.forms;
  if (!forms) return null;

  return [
    { label: 'positiv', value: forms.positive || word.text },
    { label: 'nøytr.', value: forms.neuter },
    { label: 'fl./best.', value: forms.plural || forms.definite },
    { label: 'komp.', value: forms.comparative },
    { label: 'sup. ubest.', value: forms.superlativeIndefinite },
    { label: 'sup. best.', value: forms.superlativeDefinite },
  ].filter((row) => row.value);
}

function getAdverbTooltipRows(word) {
  const forms = word.manualForms || word.forms;
  if (!forms) return null;

  return [
    { label: 'positiv', value: forms.positive || word.text },
    { label: 'komp.', value: forms.comparative },
    { label: 'sup.', value: forms.superlative },
  ].filter((row) => row.value);
}

function filterTermsByBase(terms, targetText, baseTextSet) {
  if (!(baseTextSet instanceof Set) || baseTextSet.size === 0) return terms;
  const tt = String(targetText || '').trim().toLowerCase();
  return terms.filter((t) => {
    const key = String(t || '').trim().toLowerCase();
    return key === tt || !baseTextSet.has(key);
  });
}

function collectWordTermsForHighlight(word, baseTextSet) {
  const terms = new Set();
  const add = (v) => {
    if (typeof v !== "string") return;
    const t = v.trim();
    if (t) terms.add(t);
  };
  add(word?.text);
  const forms = word?.manualForms || word?.forms;
  if (forms && typeof forms === "object") {
    Object.values(forms).forEach(add);
  }
  const filtered = filterTermsByBase([...terms], word?.text, baseTextSet);
  return filtered.sort((a, b) => b.length - a.length);
}

function renderHighlightedPhrase(phrase, word, color, baseTextSet) {
  const text = String(phrase || "");
  if (!text) return null;
  const terms = collectWordTermsForHighlight(word, baseTextSet);
  if (!terms.length || !color) return text;
  const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp("(" + terms.map(escape).join("|") + ")", "giu");
  const parts = text.split(re);
  const termsLower = new Set(terms.map((t) => t.toLowerCase()));
  return parts.map((part, i) => (
    termsLower.has(part.toLowerCase())
      ? <mark key={i} className="tooltip-phrase-mark" style={{ color, background: "transparent", fontWeight: 700 }}>{part}</mark>
      : <Fragment key={i}>{part}</Fragment>
  ));
}

function getTooltipRows(word) {
  const forms = word.manualForms || word.forms;
  if (!forms) return null;

  const hasVerbForms = ['infinitive', 'present', 'past', 'perfect', 'imperative'].some((key) => forms[key]);
  const hasNounForms = ['singularDefinite', 'pluralIndefinite', 'pluralDefinite'].some((key) => forms[key]);
  const hasAdjectiveForms = [
    'positive',
    'neuter',
    'plural',
    'definite',
    'comparative',
    'superlativeIndefinite',
    'superlativeDefinite',
  ].some((key) => forms[key]);
  const hasAdverbForms = ['positive', 'comparative', 'superlative'].some((key) => forms[key]);

  if (word.type === 'verb' && hasVerbForms) return getVerbTooltipRows(word);
  if (word.type === 'noun' && hasNounForms) return getNounTooltipRows(word);
  if (word.type === 'adjective' && hasAdjectiveForms) return getAdjectiveTooltipRows(word);
  if (word.type === 'adverb' && hasAdverbForms) return getAdverbTooltipRows(word);

  if (hasVerbForms) return getVerbTooltipRows(word);
  if (hasNounForms) return getNounTooltipRows(word);
  if (hasAdverbForms) return getAdverbTooltipRows(word);
  if (hasAdjectiveForms) return getAdjectiveTooltipRows(word);

  return null;
}

function formatInfinitiveText(text) {
  const value = (text || '').trim();
  if (!value) return '';
  return /^å\s+/i.test(value) ? value : `å ${value}`;
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function getWordSearchTerms(word, baseTextSet) {
  const forms = {
    ...(word.forms || {}),
    ...(word.manualForms || {}),
  };

  const base = [
    word.text,
    word.type === 'verb' ? formatInfinitiveText(forms.infinitive || word.text) : '',
    ...Object.values(forms),
    forms.perfect ? `har ${forms.perfect}` : '',
  ]
    .filter(Boolean)
    .map((term) => term.trim().toLowerCase())
    .filter((term, index, terms) => term && terms.indexOf(term) === index);

  return filterTermsByBase(base, word.text, baseTextSet);
}

function containsTerm(text, term) {
  const normalizedText = text.toLowerCase();
  const normalizedTerm = term.toLowerCase();

  if (normalizedTerm.includes(' ')) return normalizedText.includes(normalizedTerm);

  return new RegExp(`(^|[^\\p{L}])${escapeRegExp(normalizedTerm)}($|[^\\p{L}])`, 'u')
    .test(normalizedText);
}

function splitTextFragments(text) {
  return (text.match(/[^.!?\n]+[.!?]?/g) || [text])
    .map((fragment) => fragment.trim())
    .filter(Boolean);
}

function getTextSnippetsForWord(text, word, baseTextSet) {
  const terms = getWordSearchTerms(word, baseTextSet);
  const snippets = splitTextFragments(text)
    .filter((fragment) => terms.some((term) => containsTerm(fragment, term)));

  if (snippets.length > 0) return snippets;
  return terms.some((term) => containsTerm(text, term)) ? [text.trim()] : [];
}

function capitalizeText(text) {
  if (!text) return text;
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
}

function getWordRoots(word) {
  const roots = [
    ...(word?.root ? [{ root: word.root, translation: word.rootTranslation || '' }] : []),
    ...(Array.isArray(word?.roots) ? word.roots : []),
  ];

  return roots
    .map((item) => ({
      root: (item.root || '')
        .trim()
        .toLowerCase()
        .replace(/[^\p{L}\s]/gu, '')
        .replace(/\s+/g, ' '),
      translation: (item.translation || item.rootTranslation || '').trim().toLowerCase(),
    }))
    .filter((item) => item.root)
    .filter((item, index, items) => (
      items.findIndex((candidate) => (
        candidate.root.replace(/^å\s+/i, '').replace(/[^\p{L}]/gu, '')
        === item.root.replace(/^å\s+/i, '').replace(/[^\p{L}]/gu, '')
      )) === index
    ));
}

function getMenuPosition(event, width = 240, height = 520) {
  const margin = 12;
  const preferredHeight = Math.min(height, window.innerHeight - margin * 2);
  const x = Math.min(event.clientX, window.innerWidth - width - margin);
  const y = Math.min(event.clientY, window.innerHeight - preferredHeight - margin);
  const top = Math.max(margin, y);

  return {
    x: Math.max(margin, x),
    y: top,
    maxHeight: Math.max(220, window.innerHeight - top - margin),
  };
}

export default function CategoryPanel({
  activeCategory,
  activeSubcategory,
  db,
  categoryColors,
  categoryMeta,
  description = '',
  grammarIconCatalog,
  grammarIconParts,
  savedTexts = [],
  searchHighlight = null,
  newWord,
  newWordCapitalized,
  onBackToSubcategories,
  onClose,
  onSelectSubcategory,
  onRenameSubcategory,
  onDeleteSubcategory,
  onMarkSubcategoryAsDeck,
  onChangeCategoryColor,
  onRenameCategory,
  onDeleteCategory,
  onPinSubcategoryToConstructor,
  sectionTargets = [],
  onMoveOrCopySubcategory,
  onSetSubcategoryIcon,
  onSetDescription,
  onReorderSubcategories,
  onSelectWord,
  onSelectWordForm,
  onDeleteWord,
  onChangeWordLevel,
  onSetLevelShared,
  onSetWordGender,
  onRequestGenderBackfill,
  onRequestFormsBackfill,
  onReorderWords,
  onAddTranslation,
  onDeleteTranslation,
  onAddRelation,
  onDeleteRelation,
  onSetPhoto,
  onSetWordPhrase,
  isConstructorOpen,
  phraseDatabases = [],
  foreignPhotosCache = {},
  onLoadForeignPhoto,
  ownPublicThumbs = {},
  mySubscriptions = [],
  myUserId = null,
  onToggleSubscription,
  onToggleSubscriptionStar,
  onSetWordForms,
  onSetWordRoot,
  onDeleteWordRoot,
  onMoveOrCopyWord,
  onEditWordText,
  onOpenEditWordModal,
  onRequestPhoto,
  currentCategoryType,
  isPhraseContext,
  categoryOrder = [],
  onNewWordChange,
  onNewWordCapitalizedChange,
  onAddNode,
  onAddSubcategory,
  onOpenAddRowModal,
  onOpenDeleteRowModal,
  activeSubcategoryRowsMeta = { rows: [], rowOf: {} },
  onMoveSubcategoryRow,
  onSetWordRow,
  onOpenWordLinkModal,
  onOpenWordStoryModal,
  getWordTexts,
  onOpenStoryInAnalyzer,
  onRenameSubcategoryRow,
  onOpenRenameRowModal,
  onAssignSubcategoryRow,
  onOpenOrdskjonnhet,
  onViewPublic,
  requestConfirm = (m) => Promise.resolve(window.confirm(m)),
}) {
  const [wordMenu, setWordMenu] = useState(null);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [draftPhotoPosition, setDraftPhotoPosition] = useState(null);
  // Сбрасываем черновик положения фото при смене активного слова в меню
  useEffect(() => { setDraftPhotoPosition(null); }, [wordMenu?.index]);
  const [subcategoryMenu, setSubcategoryMenu] = useState(null);
  const [deckConfirmPath, setDeckConfirmPath] = useState(null);
  const [subcategoryNameDraft, setSubcategoryNameDraft] = useState('');
  const [activeSectionTransferMode, setActiveSectionTransferMode] = useState(null);
  const [activeTooltipKey, setActiveTooltipKey] = useState(null);
  const [cardIndexByKey, setCardIndexByKey] = useState({});
  const [flippedByKey, setFlippedByKey] = useState({});
  const [tooltipCloseTimer, setTooltipCloseTimer] = useState(null);
  const [isAddingTranslation, setIsAddingTranslation] = useState(false);
  const [activeRelationForm, setActiveRelationForm] = useState(null);
  const [activeTransferMode, setActiveTransferMode] = useState(null);
  const [transferScope, setTransferScope] = useState(null);
  const [translationDraft, setTranslationDraft] = useState('');
  const [relationDraft, setRelationDraft] = useState('');
  const [isAddingPhrase, setIsAddingPhrase] = useState(false);
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const [photoAlert, setPhotoAlert] = useState(null);
  const tapTimerRef = useRef(null);
  const wordMenuRef = useRef(null);
  const subcategoryMenuRef = useRef(null);
  const subLongPressTimerRef = useRef(null);

  useEffect(() => {
    const el = wordMenuRef.current;
    if (!el || !wordMenu) return undefined;
    const gap = 16;
    const clamp = () => {
      const vh = window.innerHeight;
      el.style.maxHeight = (vh - gap * 2) + 'px';
      const h = el.getBoundingClientRect().height;
      let top = wordMenu.y;
      if (top + h > vh - gap) top = Math.max(gap, vh - h - gap);
      el.style.top = top + 'px';
    };
    clamp();
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(clamp);
      ro.observe(el);
    }
    window.addEventListener('resize', clamp);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', clamp);
    };
  }, [wordMenu]);

  useEffect(() => {
    const el = subcategoryMenuRef.current;
    if (!el || !subcategoryMenu) return undefined;
    const gap = 16;
    const clamp = () => {
      const vh = window.innerHeight;
      el.style.maxHeight = (vh - gap * 2) + 'px';
      const h = el.getBoundingClientRect().height;
      let top = subcategoryMenu.y;
      if (top + h > vh - gap) top = Math.max(gap, vh - h - gap);
      el.style.top = top + 'px';
    };
    clamp();
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(clamp);
      ro.observe(el);
    }
    window.addEventListener('resize', clamp);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', clamp);
    };
  }, [subcategoryMenu]);


  const wordLongPressTimerRef = useRef(null);
  const wordLongPressFiredRef = useRef(false);
  const activeTooltipKeyRef = useRef(null);
  useEffect(() => { activeTooltipKeyRef.current = activeTooltipKey; }, [activeTooltipKey]);

  const handledMenuHighlightRef = useRef(0);
  useEffect(() => {
    const sh = searchHighlight;
    if (!sh || !sh.openMenu || !activeCategory) return;
    if (sh.category !== activeCategory) return;
    if ((sh.subcategory || null) !== (activeSubcategory || null)) return;
    if (handledMenuHighlightRef.current === sh.createdAt) return;
    const node = getNodeAtPath(db[activeCategory], activeSubcategory);
    const ws = node ? getWordsFromNode(node) : [];
    const target = (sh.word || '').trim().toLowerCase();
    const index = ws.findIndex((w) => (w.text || '').trim().toLowerCase() === target);
    if (index < 0) return;
    handledMenuHighlightRef.current = sh.createdAt;
    const x = sh.x || Math.round(window.innerWidth / 2);
    const y = sh.y || 140;
    openWordMenu({ clientX: x, clientY: y, preventDefault() {}, stopPropagation() {} }, ws[index], index);
  }, [searchHighlight, activeCategory, activeSubcategory, db]);

  const isMobileView = () => typeof window !== 'undefined' && window.matchMedia('(max-width: 720px)').matches;
  const [phraseDraft, setPhraseDraft] = useState('');
  const [isEditingForms, setIsEditingForms] = useState(false);
  const [formsDraft, setFormsDraft] = useState({});
  const [isEditingRoot, setIsEditingRoot] = useState(false);
  const [rootDraft, setRootDraft] = useState('');
  const [rootTranslationDraft, setRootTranslationDraft] = useState('');
  const [draggedWordIndex, setDraggedWordIndex] = useState(null);
  const [dropIndicatorIndex, setDropIndicatorIndex] = useState(null);
  const [draggedSubcategoryIndex, setDraggedSubcategoryIndex] = useState(null);
  const [subcategoryDropIndex, setSubcategoryDropIndex] = useState(null);
  const [newSubcategoryName, setNewSubcategoryName] = useState('');
  const [usagePanel, setUsagePanel] = useState(null);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [translationTooltip, setTranslationTooltip] = useState(null);

  useEffect(() => {
    const closeMenu = () => {
      setActiveSectionTransferMode(null);
      setIsAddingTranslation(false);
      setActiveRelationForm(null);
      setActiveTransferMode(null);
      setTranslationDraft('');
      setRelationDraft('');
      setIsEditingForms(false);
      setFormsDraft({});
      setIsEditingRoot(false);
      setRootDraft('');
      setRootTranslationDraft('');
      setTranslationTooltip(null);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        closeMenu();
        setWordMenu(null);
        setSubcategoryMenu(null);
        setSubcategoryNameDraft('');
      }
    };
    const closeOnOutsideScroll = (event) => {
      if (event.target?.closest?.('.word-context-menu')) return;
      closeMenu();
    };

    window.addEventListener('click', closeMenu);
    window.addEventListener('scroll', closeOnOutsideScroll, true);
    window.addEventListener('keydown', closeOnEscape);

    return () => {
      window.removeEventListener('click', closeMenu);
      window.removeEventListener('scroll', closeOnOutsideScroll, true);
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, []);

  useEffect(() => () => {
    if (tooltipCloseTimer) clearTimeout(tooltipCloseTimer);
  }, [tooltipCloseTimer]);

  useEffect(() => {
    if (!wordMenu && !subcategoryMenu) return undefined;
    const onDocPointer = (e) => {
      if (e.target && e.target.closest && e.target.closest('.word-context-menu')) return;
      setWordMenu(null);
      setSubcategoryMenu(null);
    };
    const id = window.setTimeout(() => document.addEventListener('pointerdown', onDocPointer), 0);
    return () => { window.clearTimeout(id); document.removeEventListener('pointerdown', onDocPointer); };
  }, [wordMenu, subcategoryMenu]);

  useEffect(() => {
    if (!onRequestPhoto) return;
    const requested = new Set();
    const reqPhoto = (ph) => {
      if (!ph) return;
      const key = ph.photoKey;
      if (!key || requested.has(key) || ph.dataUrl) return;
      requested.add(key);
      onRequestPhoto(key);
    };
    const visitWord = (w) => {
      if (!w) return;
      reqPhoto(w.photo);
      if (w.levels && typeof w.levels === 'object') {
        Object.keys(w.levels).forEach((lv) => reqPhoto(w.levels[lv] && w.levels[lv].photo));
      }
    };
    const node = activeSubcategory
      ? normalizeNode(getNodeAtPath(db[activeCategory], activeSubcategory))
      : normalizeNode(db[activeCategory] || {});
    getWordsFromNode(node).forEach(visitWord);
  }, [db, activeCategory, activeSubcategory, onRequestPhoto]);

  if (!activeCategory) return null;

  const activePath = pathToArray(activeSubcategory);
  const activeNode = activeSubcategory
    ? normalizeNode(getNodeAtPath(db[activeCategory], activeSubcategory))
    : normalizeNode(db[activeCategory] || {});
  const subcategories = getSubcategoryNames(activeNode);
  const words = getWordsFromNode(activeNode);
  const supportsWordMenu = Boolean(activeCategory);
  const menuTargetSubcategories = collectSubcategoryPaths(db[activeCategory] || {})
    .filter((subcategoryPath) => subcategoryPath !== activeSubcategory);
  const currentParentPath = arrayToPath(activePath);
  const breadcrumbItems = activePath.map((part, index) => ({
    label: part,
    path: arrayToPath(activePath.slice(0, index + 1)),
  }));

  const getChildPath = (subcategory) => arrayToPath([...activePath, subcategory]);
  const getPathLabel = (path) => [activeCategory, ...pathToArray(path)].filter(Boolean).join(' / ');
  const getSectionTargets = (scope) => {
    if (!subcategoryMenu) return [];

    return sectionTargets.filter((target) => {
      if (scope === 'local') {
        if (target.category !== activeCategory) return false;
      } else if (scope === 'global') {
        if (target.category === activeCategory) return false;
        if (target.path) return false;
        return true;
      }
      if (!target.path) return true;
      return target.path !== subcategoryMenu.path
        && !target.path.startsWith(`${subcategoryMenu.path}${PATH_SEPARATOR}`);
    });
  };

  const submitNestedSubcategory = (event) => {
    event.preventDefault();
    const cleanName = newSubcategoryName.trim();
    if (!cleanName) return;

    onAddSubcategory(cleanName, currentParentPath);
    setNewSubcategoryName('');
  };

  const currentNodeHasSubcategories = subcategories.length > 0;
  const shouldShowWordAdd = Boolean(activeSubcategory);
  const shouldShowSubcategoryAdd = Boolean(!activeSubcategory || onAddSubcategory);
  const isPhraseCategory = activeCategory === 'Фразы';
  const isPhraseContextEff = !!isPhraseContext || isPhraseCategory;
  const wordInputMaxLength = isPhraseCategory ? 240 : 80;
  const translationMaxLength = isPhraseCategory ? 240 : 40;
  const dictionaryBaseTexts = collectDictionaryBaseTexts(db);
  const wordTextMatches = wordMenu
    ? savedTexts
      .map((savedText) => ({
        ...savedText,
        snippets: getTextSnippetsForWord(savedText.text || '', wordMenu.word, dictionaryBaseTexts),
      }))
      .filter((savedText) => savedText.snippets.length > 0)
      .sort((left, right) => (
        (right.photoScore || 0) - (left.photoScore || 0)
        || new Date(right.updatedAt || right.createdAt || 0) - new Date(left.updatedAt || left.createdAt || 0)
        || String(left.id || '').localeCompare(String(right.id || ''))
      ))
    : [];

  const findGrammarIcon = (iconId) => {
    const groups = Object.values(grammarIconCatalog || {});
    const iconGroups = groups.flatMap((group) => Object.values(group || {}));
    return iconGroups.flat().find((icon) => icon.id === iconId);
  };

  const getSubcategoryIcon = (subcategoryPath) => {
    const meta = categoryMeta?.[`${activeCategory}::${subcategoryPath}`];
    return meta?.iconId ? findGrammarIcon(meta.iconId) : null;
  };

  const openSubcategoryMenu = (event, subcategory) => {
    event.preventDefault();
    event.stopPropagation();
    const subcategoryPath = getChildPath(subcategory);
    const position = getMenuPosition(event, 260, 420);
    setSubcategoryMenu({
      name: subcategory,
      path: subcategoryPath,
      x: position.x,
      y: position.y,
      maxHeight: position.maxHeight,
    });
    setSubcategoryNameDraft(subcategory);
  };

  const startSubLongPress = (event, subcategory) => {
    const ev = { preventDefault(){}, stopPropagation(){}, clientX: event.clientX, clientY: event.clientY };
    if (subLongPressTimerRef.current) window.clearTimeout(subLongPressTimerRef.current);
    subLongPressTimerRef.current = window.setTimeout(() => {
      subLongPressTimerRef.current = null;
      openSubcategoryMenu(ev, subcategory);
    }, 500);
  };

  const cancelSubLongPress = () => {
    if (subLongPressTimerRef.current) {
      window.clearTimeout(subLongPressTimerRef.current);
      subLongPressTimerRef.current = null;
    }
  };

  const handleSubcategoryClick = (childPath, subcategory) => {
    if (subcategoryMenu && subcategoryMenu.name === subcategory) return;
    onSelectSubcategory(childPath);
  };

  const submitSubcategoryRename = (event) => {
    event.preventDefault();
    if (!subcategoryMenu) return;

    onRenameSubcategory(subcategoryMenu.path, subcategoryNameDraft);
    setSubcategoryMenu(null);
    setSubcategoryNameDraft('');
  };


  const makeSubcategoryDeck = () => {
    if (!subcategoryMenu) return;
    setDeckConfirmPath(subcategoryMenu.path);
    setSubcategoryMenu(null);
    setSubcategoryNameDraft('');
  };

  const confirmMakeDeck = () => {
    if (deckConfirmPath && onMarkSubcategoryAsDeck) onMarkSubcategoryAsDeck(deckConfirmPath);
    setDeckConfirmPath(null);
  };

  const deleteSubcategory = () => {
    if (!subcategoryMenu) return;

    onDeleteSubcategory(subcategoryMenu.path);
    setSubcategoryMenu(null);
    setSubcategoryNameDraft('');
    setActiveSectionTransferMode(null);
  };

  const setSubcategoryIcon = (iconId) => {
    if (!subcategoryMenu) return;

    onSetSubcategoryIcon(subcategoryMenu.path, iconId);
    setSubcategoryMenu(null);
    setSubcategoryNameDraft('');
  };

  const pinSubcategoryToConstructor = () => {
    if (!subcategoryMenu || !onPinSubcategoryToConstructor) return;

    onPinSubcategoryToConstructor({
      category: activeCategory,
      path: subcategoryMenu.path,
      label: subcategoryMenu.name,
    });
    setSubcategoryMenu(null);
    setSubcategoryNameDraft('');
    setActiveSectionTransferMode(null);
  };

  const applySectionAction = (target, mode) => {
    if (!subcategoryMenu) return;

    onMoveOrCopySubcategory(subcategoryMenu.path, target, mode);
    setSubcategoryMenu(null);
    setSubcategoryNameDraft('');
    setActiveSectionTransferMode(null);
  };

  const openDescriptionEditor = () => {
    setDescriptionDraft(description || '');
    setIsEditingDescription(true);
  };


  const submitDescription = (event) => {
    event.preventDefault();
    onSetDescription(descriptionDraft);
    setIsEditingDescription(false);
  };

  const handleWordTap = (event, word, index, tooltipKey) => {
    if (wordLongPressFiredRef.current) {
      wordLongPressFiredRef.current = false;
      return;
    }
    if (isConstructorOpen) {
      onSelectWord(word, activeCategory);
      return;
    }
    if (!isMobileView()) {
      onSelectWord(word, activeCategory);
    }
    if (activeTooltipKeyRef.current === tooltipKey) {
      if (tooltipCloseTimer) clearTimeout(tooltipCloseTimer);
      setActiveTooltipKey(null);
    } else {
      keepTooltipOpen(tooltipKey);
    }
  };

  const openWordMenu = (event, word, index) => {
    if (!supportsWordMenu) return;

    event.preventDefault();
    event.stopPropagation();
    const position = getMenuPosition(event, 240, 560);
    const menuLevel = LEVEL_ORDER.includes(word.lvl) ? word.lvl : 'A1';
    const normalizedWord = applyWordLevel(word, buildLevels(word), menuLevel);
    setWordMenu({
      x: position.x,
      y: position.y,
      maxHeight: position.maxHeight,
      word: normalizedWord,
      index,
    });
    if (onRequestGenderBackfill
        && (currentCategoryType ? currentCategoryType === 'noun' : word.type === 'noun')
        && !word.gender) {
      onRequestGenderBackfill(word, index);
    }
    if (onRequestFormsBackfill) {
      const existingForms = { ...(word.forms || {}), ...(word.manualForms || {}) };
      if (!Object.values(existingForms).some(Boolean)) {
        onRequestFormsBackfill(word, index);
      }
    }
    setIsAddMenuOpen(false);
    setIsAddingPhrase(false);
    setIsAddingTranslation(false);
    setActiveRelationForm(null);
    setActiveTransferMode(null);
    setActiveTooltipKey(null);
    setTranslationDraft('');
    setRelationDraft('');
    setIsEditingForms(false);
    setFormsDraft({});
    setIsEditingRoot(false);
    setRootDraft('');
    setRootTranslationDraft('');
  };

  const startWordLongPress = (event, word, index, tooltipKey) => {
    if (!isMobileView()) return;
    const ev = { preventDefault(){}, stopPropagation(){}, clientX: event.clientX, clientY: event.clientY };
    if (wordLongPressTimerRef.current) window.clearTimeout(wordLongPressTimerRef.current);
    wordLongPressTimerRef.current = window.setTimeout(() => {
      wordLongPressTimerRef.current = null;
      wordLongPressFiredRef.current = true;
      if (isConstructorOpen) {
        keepTooltipOpen(tooltipKey);
      } else {
        openWordMenu(ev, word, index);
      }
    }, 500);
  };

  const cancelWordLongPress = () => {
    if (wordLongPressTimerRef.current) {
      window.clearTimeout(wordLongPressTimerRef.current);
      wordLongPressTimerRef.current = null;
    }
  };

  const activeSubcategoryName = activePath.at(-1) || '';
  const activeCategoryKey = (activeCategory || '').trim().toLowerCase();
  const activeSubcategoryKey = activeSubcategoryName.trim().toLowerCase();
  const nounContextCategories = [
    'субъект',
    'объект',
    'предмет',
    'предметы',
    'person',
    'personer',
    'persons',
    'people',
  ];
  const nonNounSubcategories = ['местоимения', 'pronouns'];
  const nounContextSubcategories = [
    'люди',
    'предметы',
    'предмет',
    'объекты',
    'объект',
    'person',
    'personer',
    'persons',
    'people',
    'mennesker',
  ];
  const isNounContext = (
    nounContextCategories.includes(activeCategoryKey)
    && !nonNounSubcategories.includes(activeSubcategoryKey)
  ) || nounContextSubcategories.includes(activeSubcategoryKey) || words.some((word) => word.type === 'noun');
  const isVerbContext = ['глагол', 'verb', 'verbs'].includes(activeCategoryKey);
  const isAdjectiveContext = ['прилагательное', 'adjective', 'adjectives'].includes(activeCategoryKey);
  const isAdverbContext = ['наречие', 'наречия', 'adverb', 'adverbs'].includes(activeCategoryKey);
  const isNounWord = wordMenu?.word?.type === 'noun' || (!wordMenu?.word?.type && isNounContext);
  const isVerbWord = wordMenu?.word?.type === 'verb' || (!wordMenu?.word?.type && isVerbContext);
  const isAdjectiveWord = wordMenu?.word?.type === 'adjective' || (!wordMenu?.word?.type && isAdjectiveContext);
  const isAdverbWord = wordMenu?.word?.type === 'adverb' || (!wordMenu?.word?.type && isAdverbContext);
  const canEditForms = isNounWord || isVerbWord || isAdjectiveWord || isAdverbWord;
  const speakNorwegian = (text) => {
    const t = (text == null ? '' : String(text)).trim();
    if (!t) return;
    try {
      const audio = new Audio('/tts/synth?text=' + encodeURIComponent(t));
      audio.play().catch(() => {});
    } catch (e) { /* tts unavailable */ }
  };
  const playMenuWordAudio = () => {
    if (!wordMenu || !wordMenu.word) return;
    let t = (wordMenu.word.text || '').trim();
    if (!t) return;
    if (isVerbWord && !/^å\s+/i.test(t)) t = 'å ' + t;
    else if (isNounWord && wordMenu.word.gender) t = wordMenu.word.gender + ' ' + t;
    speakNorwegian(t);
  };

  const applyWordGender = (gender) => {
    if (!wordMenu) return;
    if (onSetWordGender) onSetWordGender(wordMenu.index, gender);
    setWordMenu((currentMenu) => (
      currentMenu ? { ...currentMenu, word: { ...currentMenu.word, gender: gender || null } } : currentMenu
    ));
  };

  const changeWordLevel = (level) => {
    if (!wordMenu) return;

    onChangeWordLevel(wordMenu.index, level);
    setWordMenu((currentMenu) => {
      if (!currentMenu) return currentMenu;
      const levels = buildLevels(currentMenu.word);
      return { ...currentMenu, word: applyWordLevel(currentMenu.word, levels, level) };
    });
    setPhraseDraft(getPhraseForLevel(wordMenu.word, level));
    setIsAddingPhrase(false);
  };

  const toggleShareLevel = () => {
    if (!wordMenu) return;
    const current = LEVEL_ORDER.includes(wordMenu.word.lvl) ? wordMenu.word.lvl : 'A1';
    const levels = buildLevels(wordMenu.word);
    const wasShared = !!(levels[current] && levels[current].shared);
    const nextShared = !wasShared;
    onSetLevelShared(wordMenu.index, current, nextShared);
    setWordMenu((m) => {
      if (!m) return m;
      const lv = buildLevels(m.word);
      if (!lv[current]) lv[current] = { phrase: '', photo: null };
      lv[current] = { ...lv[current], shared: nextShared };
      return { ...m, word: applyWordLevel(m.word, lv, current) };
    });
  };

  const applyMenuAction = (targetSubcategory, mode, targetCategory) => {
    if (!wordMenu) return;

    onMoveOrCopyWord(wordMenu.index, targetSubcategory, mode, targetCategory);
    setWordMenu(null);
    setActiveTransferMode(null);
    setTransferScope(null);
  };

  const updateWordMenuList = (key, updater) => {
    setWordMenu((currentMenu) => {
      if (!currentMenu) return currentMenu;

      return {
        ...currentMenu,
        word: {
          ...currentMenu.word,
          [key]: updater(currentMenu.word[key] || []),
        },
      };
    });
  };

  const submitTranslation = (event) => {
    event.preventDefault();
    if (!wordMenu) return;

    const cleanDraft = translationDraft.trim();
    onAddTranslation(wordMenu.index, cleanDraft);
    setTranslationDraft('');
    updateWordMenuList('translations', (items) => [...items, cleanDraft].filter(Boolean).slice(0, 5));
  };

  const deleteTranslation = (translationIndex) => {
    if (!wordMenu) return;

    onDeleteTranslation(wordMenu.index, translationIndex);
    updateWordMenuList('translations', (items) => items.filter((_, index) => index !== translationIndex));
  };

  const showTranslationTooltip = (event, translation) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTranslationTooltip({
      text: translation,
      x: Math.max(12, Math.min(rect.left, window.innerWidth - 376)),
      y: Math.max(12, rect.top - 10),
    });
  };

  const submitRelation = (event) => {
    event.preventDefault();
    if (!wordMenu || !activeRelationForm) return;

    const cleanDraft = relationDraft.trim();
    onAddRelation(wordMenu.index, activeRelationForm, cleanDraft);
    setRelationDraft('');
    updateWordMenuList(activeRelationForm, (items) => [...items, cleanDraft].filter(Boolean).slice(0, 3));
  };

  const deleteRelation = (relationKey, valueIndex) => {
    if (!wordMenu) return;

    onDeleteRelation(wordMenu.index, relationKey, valueIndex);
    updateWordMenuList(relationKey, (items) => items.filter((_, index) => index !== valueIndex));
  };

  const showPhraseForm = () => {
    if (!wordMenu) return;
    setIsAddingTranslation(false);
    setActiveRelationForm(null);
    setTranslationDraft('');
    setRelationDraft('');
    setIsEditingForms(false);
    setIsEditingRoot(false);
    setActiveTransferMode(null);
    setPhraseDraft(getPhraseForLevel(wordMenu.word, wordMenu.word.lvl || 'A1'));
    setIsAddingPhrase(true);
  };

  const submitPhrase = (event) => {
    event.preventDefault();
    if (!wordMenu) return;
    const clean = phraseDraft.trim().slice(0, 200);
    onSetWordPhrase(wordMenu.index, clean);
    patchMenuLevel({ phrase: clean });
    setIsAddingPhrase(false);
  };

  const showRelationForm = (relationKey) => {
    setIsAddingTranslation(false);
    setTranslationDraft('');
    setIsEditingForms(false);
    setIsEditingRoot(false);
    setRootDraft('');
    setRootTranslationDraft('');
    setActiveTransferMode(null);
    setActiveRelationForm(relationKey);
    setRelationDraft('');
  };

  const showFormsForm = () => {
    if (!wordMenu) return;

    setIsAddingTranslation(false);
    setActiveRelationForm(null);
    setTranslationDraft('');
    setRelationDraft('');
    setIsEditingForms(true);
    setIsEditingRoot(false);
    setRootDraft('');
    setRootTranslationDraft('');
    setFormsDraft(wordMenu.word.manualForms || wordMenu.word.forms || {});
  };

  const showRootForm = () => {
    if (!wordMenu) return;

    setIsAddingTranslation(false);
    setActiveRelationForm(null);
    setTranslationDraft('');
    setRelationDraft('');
    setIsEditingForms(false);
    setFormsDraft({});
    setIsEditingRoot(true);
    setRootDraft('');
    setRootTranslationDraft('');
  };

  const submitRoot = (event) => {
    event.preventDefault();
    if (!wordMenu) return;

    const cleanRoot = rootDraft
      .trim()
      .toLowerCase()
      .replace(/[^\p{L}\s]/gu, '')
      .replace(/\s+/g, ' ');
    const cleanRootTranslation = rootTranslationDraft.trim().toLowerCase();
    const didSave = onSetWordRoot(wordMenu.index, cleanRoot, cleanRootTranslation);
    if (!didSave) return;

    setWordMenu((currentMenu) => {
      if (!currentMenu) return currentMenu;

      const currentRoots = Array.isArray(currentMenu.word.roots) ? currentMenu.word.roots : [];

      return {
        ...currentMenu,
        word: {
          ...currentMenu.word,
          roots: [
            ...currentRoots,
            {
              root: cleanRoot,
              translation: cleanRootTranslation,
            },
          ],
        },
      };
    });
    setIsEditingRoot(false);
    setRootDraft('');
    setRootTranslationDraft('');
  };

  const deleteRoot = (rootValue) => {
    if (!wordMenu) return;

    onDeleteWordRoot(wordMenu.index, rootValue);
    setWordMenu((currentMenu) => {
      if (!currentMenu) return currentMenu;

      return {
        ...currentMenu,
        word: {
          ...currentMenu.word,
          root: currentMenu.word.root === rootValue ? '' : currentMenu.word.root,
          rootTranslation: currentMenu.word.root === rootValue ? '' : currentMenu.word.rootTranslation,
          roots: getWordRoots(currentMenu.word).filter((item) => item.root !== rootValue),
        },
      };
    });
  };

  const updateFormsDraft = (key, value) => {
    setFormsDraft((currentDraft) => ({
      ...currentDraft,
      [key]: value,
    }));
  };

  const submitForms = (event) => {
    event.preventDefault();
    if (!wordMenu) return;

    onSetWordForms(wordMenu.index, formsDraft);
    setWordMenu((currentMenu) => {
      if (!currentMenu) return currentMenu;
      const cleanFormsDraft = Object.entries(formsDraft || {}).reduce((nextForms, [key, value]) => {
        const cleanValue = key === 'infinitive'
          ? value.trim().toLowerCase().replace(/^å\s+/i, '')
          : value.trim().toLowerCase();
        return cleanValue ? { ...nextForms, [key]: cleanValue } : nextForms;
      }, {});
      const hasForms = Object.keys(cleanFormsDraft).length > 0;
      const inferredType = currentMenu.word.type
        || (isNounContext ? 'noun' : null)
        || (isVerbContext ? 'verb' : null)
        || (isAdjectiveContext ? 'adjective' : null)
        || (isAdverbContext ? 'adverb' : null);

      return {
        ...currentMenu,
        word: {
          ...currentMenu.word,
          type: inferredType,
          text: inferredType === 'verb' && cleanFormsDraft.infinitive
            ? cleanFormsDraft.infinitive
            : ['adjective', 'adverb'].includes(inferredType) && cleanFormsDraft.positive
              ? cleanFormsDraft.positive
              : currentMenu.word.text,
          forms: hasForms ? cleanFormsDraft : null,
          manualForms: hasForms ? cleanFormsDraft : null,
          formsSource: hasForms ? 'manual' : null,
        },
      };
    });
    setIsEditingForms(false);
    setFormsDraft({});
  };

  const patchMenuLevel = (patch) => {
    setWordMenu((currentMenu) => {
      if (!currentMenu) return currentMenu;
      const w = currentMenu.word;
      const lvl = LEVEL_ORDER.includes(w.lvl) ? w.lvl : 'A1';
      const levels = buildLevels(w);
      levels[lvl] = { ...(levels[lvl] || { phrase: '', photo: null }), ...patch };
      return { ...currentMenu, word: applyWordLevel(w, levels, lvl) };
    });
  };

  const updateWordMenuPhoto = (photo) => {
    patchMenuLevel({ photo });
  };

  const addPhoto = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !wordMenu) return;

    const wordIndex = wordMenu.index;

    try {
      const photo = await createCompactPhoto(file);
      const freshKey = 'ph_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
      const photoToSave = { ...photo, photoKey: freshKey };
      onSetPhoto(wordIndex, photoToSave);
      updateWordMenuPhoto(photoToSave);
    } catch {
      setPhotoAlert('Фото слишком большое для базы. Попробуй выбрать картинку меньшего размера или предварительно сжать её.');
    }

    event.target.value = '';
  };

  const deletePhoto = () => {
    if (!wordMenu) return;

    onSetPhoto(wordMenu.index, null);
    updateWordMenuPhoto(null);
  };

  const updatePhotoPosition = (pos) => {
    if (!wordMenu) return;
    const w = wordMenu.word;
    const base = (w.photo && w.photo.dataUrl) ? w.photo
      : (w.ord && w.ord.photo && w.ord.photo.dataUrl) ? w.ord.photo
      : publicThumbFor(w);
    if (!base || !base.dataUrl) return;
    const next = { ...base, position: pos };
    onSetPhoto(wordMenu.index, next);
    updateWordMenuPhoto(next);
  };

  const deleteWordFromMenu = async () => {
    if (!wordMenu) return;
    const confirmed = await requestConfirm('Удалить слово "' + wordMenu.word.text + '"? Это действие нельзя отменить.');
    if (!confirmed) return;
    onDeleteWord(wordMenu.index);
    setWordMenu(null);
  };

  const showWordUsage = (savedText) => {
    if (!wordMenu) return;

    setUsagePanel({
      textId: savedText.id,
      word: wordMenu.word.text,
      snippets: savedText.snippets,
    });
    setWordMenu(null);
  };

  // Показ предложений из историй по очереди: один тап — одно предложение,
  // следующий тап — следующее (того же или другого слова), по кругу.
  const openUsage = (usages, startIndex) => {
    if (!usages || usages.length === 0) return;
    setUsagePanel({ usages, index: startIndex || 0 });
    setWordMenu(null);
  };

  const getDisplayText = (word) => {
    const forms = word.manualForms || word.forms;
    const effectiveType = currentCategoryType ? currentCategoryType : word.type;
    const text = effectiveType === 'verb'
      ? formatInfinitiveText(forms?.infinitive || word.text)
      : word.text;

    return word.capitalized ? capitalizeText(text) : text;
  };

  const getNounArticle = (word) => {
    const effectiveType = currentCategoryType ? currentCategoryType : word.type;
    return effectiveType === 'noun' && word.gender ? word.gender : '';
  };
  const _isDarkTheme = typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark';
  const GENDER_COLORS = { en: '#2563eb', ei: '#be185d', et: _isDarkTheme ? '#7c8899' : '#475569' };
  const getPillColor = (word) => {
    const article = getNounArticle(word);
    return article ? GENDER_COLORS[article] : categoryColors[activeCategory];
  };

  const keepTooltipOpen = (tooltipKey) => {
    if (tooltipCloseTimer) clearTimeout(tooltipCloseTimer);
    setTooltipCloseTimer(null);
    setActiveTooltipKey(tooltipKey);
  };

  const scheduleTooltipClose = () => {
    if (tooltipCloseTimer) clearTimeout(tooltipCloseTimer);
    const timer = setTimeout(() => {
      setActiveTooltipKey(null);
      setTooltipCloseTimer(null);
    }, 1000);

    setTooltipCloseTimer(timer);
  };

  // Запасная миниатюра из своей публичной страницы (если локального фото нет).
  const publicThumbFor = (w) => {
    const norm = String((w && w.text) || '').trim().toLowerCase();
    const t = norm && ownPublicThumbs ? ownPublicThumbs[norm] : null;
    return t ? { dataUrl: t, name: '', type: 'image/jpeg' } : null;
  };
  const renderLinkBadge = (word) => {
    if (!word || !word.link) return null;
    const isTikTok = /tiktok\.com/i.test(word.link);
    const cls = 'tooltip-link-badge ' + (isTikTok ? 'is-tiktok' : 'is-youtube');
    const label = isTikTok ? 'Открыть в TikTok' : 'Открыть на YouTube';
    const icon = isTikTok ? (
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path fill="#fff" d="M16.5 3c.3 2.1 1.6 3.6 3.5 3.8v2.4c-1.2 0-2.4-.3-3.5-.9v5.8c0 3.2-2.6 5.4-5.5 5.4-2.7 0-5-2-5-4.9 0-3.1 2.7-5.3 6-4.7v2.6c-.4-.1-.9-.2-1.3-.2-1.3 0-2.3 1-2.3 2.3 0 1.4 1.2 2.3 2.4 2.3 1.3 0 2.4-1 2.4-2.6V3h2.8z"/></svg>
    ) : (
      <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path fill="#FF0000" d="M23 12s0-3.9-.5-5.8c-.3-1-1-1.8-2-2C18.9 3.7 12 3.7 12 3.7s-6.9 0-8.5.5c-1 .3-1.8 1-2 2C1 8.1 1 12 1 12s0 3.9.5 5.8c.3 1 1 1.8 2 2 1.6.5 8.5.5 8.5.5s6.9 0 8.5-.5c1-.3 1.7-1 2-2 .5-1.9.5-5.8.5-5.8z"/><path fill="#fff" d="M10 15.5l5-3.5-5-3.5z"/></svg>
    );
    return (
      <a href={word.link} target="_blank" rel="noopener noreferrer" className={cls} title={label} onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>{icon}</a>
    );
  };

    const renderTooltip = (word, tooltipKey) => {
    // Категория "Фразы": простая подсказка — перевод на чёрном фоне + фото (если есть).
    if (isPhraseContextEff) {
      const translation = (Array.isArray(word.translations) ? word.translations : [])
        .map((t) => String(t || '').trim()).filter(Boolean).join(', ');
      const lvl = LEVEL_ORDER.includes(word.lvl) ? word.lvl : 'A1';
      const phoLevel = getPhotoForLevel(word, lvl);
      const pho = (phoLevel && phoLevel.dataUrl) ? phoLevel
        : ((word.photo && word.photo.dataUrl) ? word.photo
        : ((word.ord && word.ord.photo && word.ord.photo.dataUrl) ? word.ord.photo : publicThumbFor(word)));
      if (!translation && !pho) return null;
      return (
        <div
          className={`noun-tooltip word-forms-tooltip phrase-translation-tooltip ${activeTooltipKey === tooltipKey ? 'is-visible' : ''}`}
          role="tooltip"
        >
          <button
            type="button"
            className="tooltip-close"
            onClick={(e) => { e.stopPropagation(); if (tooltipCloseTimer) clearTimeout(tooltipCloseTimer); setActiveTooltipKey(null); }}
            aria-label="Закрыть"
          >×</button>
          {pho && (
            <div className="tooltip-photo">
              <img src={pho.dataUrl} alt="" style={{ objectPosition: '50% ' + (pho.position ?? 50) + '%' }} />
              {renderLinkBadge(word)}
            </div>
          )}
          {translation && <div className="phrase-tip-text">{translation}</div>}
        </div>
      );
    }
    const rows = getTooltipRows(word) || [];
    const phraseCards = collectPhraseCards({
      databases: phraseDatabases,
      myId: myUserId,
      mySubscriptions,
      targetWord: word,
      baseTextSet: dictionaryBaseTexts,
    });
    const hydratedCards = phraseCards.map((c) => {
      if (!c.photo || c.photo.dataUrl) return c;
      const cacheKey = c.ownerId + ':' + c.photo.photoKey;
      const cached = foreignPhotosCache[cacheKey];
      if (cached && cached.dataUrl) {
        return { ...c, photo: { ...c.photo, dataUrl: cached.dataUrl } };
      }
      if (onLoadForeignPhoto && c.photo.photoKey && c.ownerId) {
        onLoadForeignPhoto(c.photo.photoKey, c.ownerId);
      }
      return c;
    });
    // Своё слово: карточки всех заполненных уровней (текущий — первым, как в меню),
    // затем чужие примеры. Своего нет — одна цельная чужая первой, остальные следом.
    const myAuthor = { ownerId: myUserId, ownerNickname: '', isMine: true, isSubscribed: false, isStarred: false };
    const ownLevel = LEVEL_ORDER.includes(word.lvl) ? word.lvl : 'A1';
    const ownPhraseCur = getPhraseForLevel(word, ownLevel);
    const ownPhotoCur = getPhotoForLevel(word, ownLevel);
    const hasOwnContent = !!(ownPhraseCur || ownPhotoCur?.dataUrl);
    const foreignCards = hydratedCards.filter((c) => !c.isMine);
    const ownPhraseMatchCards = hydratedCards.filter((c) => c.isMine && c.kind === 'phrase-match');
    let cardsList;
    if (hasOwnContent) {
      const activeLvls = getActiveLevels(word);
      const ordered = [ownLevel, ...LEVEL_ORDER.filter((lv) => lv !== ownLevel && activeLvls.includes(lv))]
        .filter((lv) => activeLvls.includes(lv));
      const ownCards = ordered.map((lv) => {
        const ph = getPhraseForLevel(word, lv) || '';
        const pho = getPhotoForLevel(word, lv);
        const photo = (pho && pho.dataUrl) ? pho : null;
        return {
          phrase: ph, photo,
          phraseAuthor: ph ? myAuthor : null,
          photoAuthor: photo ? myAuthor : null,
          isMine: true, ownerId: myUserId, ownerNickname: '',
          isSubscribed: false, isStarred: false, level: lv, isComposed: true,
        };
      }).filter((c) => c.phrase || c.photo);
      cardsList = [...ownCards, ...ownPhraseMatchCards, ...foreignCards];
    } else {
      const sameWordCards = foreignCards.filter((c) => c.kind === 'same-word');
      const phraseMatchCards = foreignCards.filter((c) => c.kind === 'phrase-match');
      const pick = sameWordCards.find((c) => c.phrase && c.phrase.trim() && c.photo)
        || sameWordCards.find((c) => c.photo)
        || sameWordCards.find((c) => c.phrase && c.phrase.trim())
        || phraseMatchCards.find((c) => c.phrase && c.phrase.trim());
      const baseList = pick ? [pick, ...foreignCards.filter((c) => c !== pick)] : foreignCards;
      cardsList = [...ownPhraseMatchCards, ...baseList];
    }
    // Фото из черновика (word.ord.photo) или зеркальное (word.photo) показываем
    // в словаре, если в карточках уровней своего фото ещё нет.
    {
      const ownHasPhoto = cardsList.some((c) => c.isMine && c.photo && c.photo.dataUrl);
      if (!ownHasPhoto) {
        const extraPhoto = (word.ord && word.ord.photo && word.ord.photo.dataUrl)
          ? word.ord.photo
          : ((word.photo && word.photo.dataUrl) ? word.photo : publicThumbFor(word));
        if (extraPhoto) {
          const firstOwn = cardsList.find((c) => c.isMine);
          if (firstOwn && !(firstOwn.photo && firstOwn.photo.dataUrl)) {
            firstOwn.photo = extraPhoto;
            firstOwn.photoAuthor = myAuthor;
          } else {
            cardsList = [{
              phrase: '', photo: extraPhoto,
              phraseAuthor: null, photoAuthor: myAuthor,
              isMine: true, ownerId: myUserId, ownerNickname: '',
              isSubscribed: false, isStarred: false, level: ownLevel, isComposed: true,
            }, ...cardsList];
          }
        }
      }
    }
    const cardIdx = Math.min(cardIndexByKey[tooltipKey] || 0, Math.max(0, cardsList.length - 1));
    const currentCard = cardsList[cardIdx] || null;
    const hasPhoto = !!(currentCard?.photo?.dataUrl);
    const hasTranslations = Array.isArray(word.translations) && word.translations.length > 0;
    const hasPhrase = !!(currentCard?.phrase);
    const hasFrontContent = hasPhoto || hasPhrase;
    const hasFormsContent = rows.length > 0;
    const canFlip = hasFrontContent && hasFormsContent;
    const isFlipped = canFlip && !!flippedByKey[tooltipKey];
    if (!hasFrontContent && !hasFormsContent) return null;

    let mobileStyle;
    if (isMobileView() && activeTooltipKey === tooltipKey) {
      const margin = 12;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const boxW = Math.min(vw - margin * 2, 360);
      mobileStyle = {
        position: 'fixed',
        left: '50%',
        top: 70,
        bottom: 'auto',
        transform: 'translateX(-50%)',
        width: boxW,
        maxWidth: 'none',
        maxHeight: vh - 90,
        overflowY: 'auto',
        zIndex: 100,
      };
    }

    return (
      <div
        className={`noun-tooltip word-forms-tooltip ${activeTooltipKey === tooltipKey ? 'is-visible' : ''}`}
        style={mobileStyle}
        role="tooltip"

      >
        <button
          type="button"
          className="tooltip-close"
          onClick={(e) => { e.stopPropagation(); if (tooltipCloseTimer) clearTimeout(tooltipCloseTimer); setActiveTooltipKey(null); }}
          aria-label="Закрыть"
        >
          ×
        </button>
        {(() => {
          const frontContent = hasFrontContent ? (
            <>
              {currentCard?.photo?.dataUrl && (
                <div className="tooltip-photo">
                  <img src={currentCard.photo.dataUrl} alt={currentCard.photo.name || word.text} onClick={(e) => { e.stopPropagation(); setLightboxPhoto(currentCard.photo); }} style={{cursor: 'zoom-in', objectPosition: '50% ' + (currentCard.photo.position ?? 50) + '%'}} />
                  {renderLinkBadge(word)}
                </div>
              )}
              {!hasPhrase && hasTranslations && (
                <div className="tooltip-phrase-card">
                  <div className="tooltip-phrase tooltip-phrase-translation">
                    {word.translations.map((t) => String(t || '').trim()).filter(Boolean).join(', ')}
                  </div>
                </div>
              )}
              {cardsList.length > 0 && currentCard && (hasPhrase || cardsList.length > 1) && (
                <div className="tooltip-phrase-card">
                  {cardIdx > 0 && (
                    <button type="button" className="phrase-card-arrow phrase-card-arrow-prev" onClick={(e) => { e.stopPropagation(); setCardIndexByKey((m) => ({ ...m, [tooltipKey]: cardIdx - 1 })); }} aria-label="Предыдущая">◀</button>
                  )}
                  {cardIdx < cardsList.length - 1 && (
                    <button type="button" className="phrase-card-arrow phrase-card-arrow-next" onClick={(e) => { e.stopPropagation(); setCardIndexByKey((m) => ({ ...m, [tooltipKey]: cardIdx + 1 })); }} aria-label="Следующая">▶</button>
                  )}
                  {(() => {
                    const pa = currentCard.phraseAuthor;
                    const pha = currentCard.photoAuthor;
                    const sameAuthor = pa && pha && pa.ownerId === pha.ownerId;
                    const renderActions = (a, kindLabel) => (!a || a.isMine) ? null : (
                      <div className={'phrase-card-actions ' + (kindLabel ? 'phrase-card-actions-' + kindLabel : '')}>
                        {kindLabel === 'phrase' && <span className="phrase-card-actions-icon">💬</span>}
                        {kindLabel === 'photo' && <span className="phrase-card-actions-icon">📷</span>}
                        <button type="button" className={'phrase-card-star ' + (a.isStarred ? 'is-active' : '')} onClick={(e) => { e.stopPropagation(); onToggleSubscriptionStar && onToggleSubscriptionStar(a.ownerId); }} aria-label="В избранное">★</button>
                        <button type="button" className={'phrase-card-bell ' + (a.isSubscribed ? 'is-active' : '')} onClick={(e) => { e.stopPropagation(); onToggleSubscription && onToggleSubscription(a.ownerId); }} aria-label="Подписаться"><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C10.9 2 10 2.9 10 4V4.29C7.12 5.14 5 7.82 5 11V16L3 18V19H21V18L19 16V11C19 7.82 16.88 5.14 14 4.29V4C14 2.9 13.1 2 12 2ZM10 20C10 21.1 10.9 22 12 22C13.1 22 14 21.1 14 20H10Z"/></svg></button>
                      </div>
                    );
                    if (sameAuthor) return renderActions(pa, null);
                    return (<>{renderActions(pa, 'phrase')}{renderActions(pha, 'photo')}</>);
                  })()}
                  <div className="tooltip-phrase">
                    {currentCard.phrase && renderHighlightedPhrase(currentCard.phrase, word, categoryColors?.[activeCategory], dictionaryBaseTexts)}
                    {(() => {
                      const pa = currentCard.phraseAuthor;
                      const pha = currentCard.photoAuthor;
                      const sameAuthor = pa && pha && pa.ownerId === pha.ownerId;
                      const showPhrase = pa && !pa.isMine && pa.ownerNickname;
                      const showPhoto = pha && !pha.isMine && pha.ownerNickname;
                      if (sameAuthor && showPhrase) {
                        return (<div className="tooltip-phrase-author">@{pa.ownerNickname}</div>);
                      }
                      return (
                        <>
                          {showPhrase && (<div className="tooltip-phrase-author">💬 @{pa.ownerNickname}</div>)}
                          {!sameAuthor && showPhoto && (<div className="tooltip-photo-author">📷 @{pha.ownerNickname}</div>)}
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
            </>
          ) : null;
          const backContent = hasFormsContent ? (
            <div className="tooltip-forms">
              {rows.map((row) => (
                <button className="tooltip-form-button" key={`${row.label}-${row.value}`} type="button" onClick={() => onSelectWordForm(row.value, activeCategory)}>
                  <span>{row.label}</span>
                  <b>{row.value}</b>
                </button>
              ))}
            </div>
          ) : null;
          if (canFlip) {
            return (<div className={'tooltip-flip-container ' + (isFlipped ? 'is-flipped' : '')}>
              <div className="tooltip-flip-inner">
                <div className="tooltip-flip-front">{frontContent}</div>
                <div className="tooltip-flip-back">{backContent}</div>
              </div>
              <button type="button" className="tooltip-flip-toggle" onClick={(e) => { e.stopPropagation(); setFlippedByKey((m) => ({ ...m, [tooltipKey]: !m[tooltipKey] })); }} aria-label="Перевернуть">
                {isFlipped ? '▲' : '▼'}
              </button>
            </div>);
          }
          return frontContent || backContent;
        })()}
      </div>
    );
  };

  const startWordDrag = (event, index) => {
    setDraggedWordIndex(index);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(index));
  };

  const getDropIndex = (event, index) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const isAfterMiddle = event.clientX > rect.left + rect.width / 2;
    return index + (isAfterMiddle ? 1 : 0);
  };

  const dragOverWord = (event, index) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDropIndicatorIndex(getDropIndex(event, index));
  };

  const dropWord = (event, index) => {
    event.preventDefault();
    const fromIndex = Number(event.dataTransfer.getData('text/plain'));
    const rawDropIndex = getDropIndex(event, index);
    const toIndex = fromIndex < rawDropIndex ? rawDropIndex - 1 : rawDropIndex;

    if (Number.isInteger(fromIndex)) {
      onReorderWords(fromIndex, toIndex);
    }

    setDraggedWordIndex(null);
    setDropIndicatorIndex(null);
  };

  const endWordDrag = () => {
    setDraggedWordIndex(null);
    setDropIndicatorIndex(null);
  };

  const startSubcategoryDrag = (event, index) => {
    setDraggedSubcategoryIndex(index);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(index));
  };

  const getSubcategoryDropIndex = (event, index) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const isAfterMiddle = event.clientX > rect.left + rect.width / 2;
    return index + (isAfterMiddle ? 1 : 0);
  };

  const dragOverSubcategory = (event, index) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setSubcategoryDropIndex(getSubcategoryDropIndex(event, index));
  };

  const dropSubcategory = (event, index) => {
    event.preventDefault();
    const fromIndex = Number(event.dataTransfer.getData('text/plain'));
    const rawDropIndex = getSubcategoryDropIndex(event, index);
    const toIndex = fromIndex < rawDropIndex ? rawDropIndex - 1 : rawDropIndex;

    if (Number.isInteger(fromIndex)) {
      onReorderSubcategories(fromIndex, toIndex, currentParentPath);
    }

    setDraggedSubcategoryIndex(null);
    setSubcategoryDropIndex(null);
  };

  const endSubcategoryDrag = () => {
    setDraggedSubcategoryIndex(null);
    setSubcategoryDropIndex(null);
  };

  const renderWordChip = (word, index) => {
    const tooltipKey = `${activeCategory}-${activeSubcategory||''}-${word.text}-${index}`;
    const isSearchHighlighted = searchHighlight
      && searchHighlight.category === activeCategory
      && searchHighlight.subcategory === activeSubcategory
      && (searchHighlight.word || '').trim().toLowerCase() === (word.text || '').trim().toLowerCase();
    return (
      <div
        className={[
          'word-pill',
          getNounArticle(word) ? ('gender-' + getNounArticle(word)) : '',
          isSearchHighlighted ? 'is-search-highlighted' : '',
          draggedWordIndex === index ? 'is-dragging' : '',
          dropIndicatorIndex === index ? 'drop-before' : '',
          dropIndicatorIndex === index + 1 ? 'drop-after' : '',
        ].filter(Boolean).join(' ')}
        key={`${word.text}-${index}`}
        draggable={!isTouchDevice}
        style={{ backgroundColor: getPillColor(word) }}
        onContextMenu={(event) => openWordMenu(event, word, index)}
        onPointerDown={(event) => startWordLongPress(event, word, index, tooltipKey)}
        onPointerUp={cancelWordLongPress}
        onPointerCancel={cancelWordLongPress}
        onDragStart={(event) => startWordDrag(event, index)}
        onDragOver={(event) => dragOverWord(event, index)}
        onDrop={(event) => dropWord(event, index)}
        onDragEnd={endWordDrag}
      >
      <button
        className="word-select-button"
        type="button"
        onClick={(event) => handleWordTap(event, word, index, tooltipKey)}
        aria-label={`Добавить слово ${word.text}`}
      >
        <span>{getNounArticle(word) ? <span className="word-article">{getNounArticle(word)} </span> : null}{getDisplayText(word)}</span>
      </button>
      {word.ord && (word.ord.authored || word.ord.evik) && onViewPublic && (
        <span className="word-pill-globe" role="button" title="Открыть публичную страницу" onClick={(e) => { e.stopPropagation(); onViewPublic(word); }} onPointerDown={(e) => e.stopPropagation()}>🌍</span>
      )}

        {renderTooltip(word, tooltipKey)}
      </div>
    );
  };

  const renderSubcatChip = (subcategory, index) => {
    const childPath = getChildPath(subcategory);
    const assignedIcon = getSubcategoryIcon(childPath);
    return (
      <button
        className={[
          'subcategory-button',
          assignedIcon ? 'has-grammar-icon' : '',
          draggedSubcategoryIndex === index ? 'is-dragging' : '',
          subcategoryDropIndex === index ? 'drop-before' : '',
          subcategoryDropIndex === index + 1 ? 'drop-after' : '',
        ].filter(Boolean).join(' ')}
        key={childPath}
        type="button"
        draggable={!isTouchDevice}
        title={assignedIcon ? subcategory : undefined}
        onClick={() => handleSubcategoryClick(childPath, subcategory)}
        onContextMenu={(event) => openSubcategoryMenu(event, subcategory)}
        onPointerDown={(event) => startSubLongPress(event, subcategory)}
        onPointerUp={cancelSubLongPress}
        onPointerLeave={cancelSubLongPress}
        onPointerCancel={cancelSubLongPress}
        onDragStart={(event) => startSubcategoryDrag(event, index)}
        onDragOver={(event) => dragOverSubcategory(event, index)}
        onDrop={(event) => dropSubcategory(event, index)}
        onDragEnd={endSubcategoryDrag}
      >
        {assignedIcon ? (
          <span className="grammar-icon" aria-label={assignedIcon.label}>
            {assignedIcon.symbol}
          </span>
        ) : subcategory}
      </button>
    );
  };

  const renderSubcategoryAddForm = () => (
    <form
      className="quick-add subcategory-add header-subcategory-add"
      onSubmit={(event) => {
        event.preventDefault();
        if (activeSubcategory) {
          submitNestedSubcategory(event);
        } else {
          onAddNode();
        }
      }}
    >
      <input
        value={activeSubcategory ? newSubcategoryName : newWord}
        onChange={(event) => (
          activeSubcategory
            ? setNewSubcategoryName(event.target.value)
            : onNewWordChange(event.target.value)
        )}
        placeholder="Новая подкатегория..."
      />
      <button
        type="submit"
        aria-label="Добавить подкатегорию"
      >
        +
      </button>
    </form>
  );

  return (
    <section
      className="category-panel"
      style={{ borderColor: categoryColors[activeCategory] }}
      aria-label={`Категория ${activeCategory}`}
    >
      <button
        type="button"
        className="category-panel-close"
        onClick={onClose}
        aria-label="Закрыть"
      >
        ×
      </button>
      <header className="category-panel-header">
        <div className="category-title-row">
          <h2 style={{ color: categoryColors[activeCategory] }}>
            <button type="button" onClick={onBackToSubcategories}>
              {activeCategory}
            </button>
            {breadcrumbItems.map((item) => (
              <span className="category-breadcrumb-item" key={item.path}>
                <span className="category-breadcrumb-separator"> / </span>
                <button type="button" onClick={() => onSelectSubcategory(item.path)}>
                  {item.label}
                </button>
              </span>
            ))}
          </h2>
          {shouldShowSubcategoryAdd && renderSubcategoryAddForm()}
        </div>
        <div className="category-panel-actions">
          {activeCategory && (
            <span className="subcat-row-controls">
              <span className="subcat-row-controls-label">Рядок</span>
              <button type="button" className="subcat-row-btn" title="Добавить рядок" onClick={() => onOpenAddRowModal && onOpenAddRowModal(activeSubcategory || '')}>+</button>
              <button type="button" className="subcat-row-btn" title="Удалить рядок" onClick={() => onOpenDeleteRowModal && onOpenDeleteRowModal(activeSubcategory || '')}>−</button>
            </span>
          )}
          <button className="category-description-link" type="button" onClick={openDescriptionEditor}>
            {description ? 'Изменить описание' : 'Добавить описание'}
          </button>
        </div>
      </header>

      {description && !isEditingDescription && (
        <p className="category-description">{description}</p>
      )}

      {isEditingDescription && (
        <form className="category-description-editor" onSubmit={submitDescription}>
          <textarea
            value={descriptionDraft}
            onChange={(event) => setDescriptionDraft(event.target.value)}
            placeholder="Добавьте короткое описание..."
            maxLength={600}
            autoFocus
          />
          <div>
            <button type="button" onClick={() => setIsEditingDescription(false)}>
              Отмена
            </button>
            <button type="submit">
              Сохранить
            </button>
          </div>
        </form>
      )}

      {activeSubcategoryRowsMeta.rows.length > 0 && activeSubcategoryRowsMeta.rows.map((rowName, ri, arr) => (
        <div className="subcat-row-section" key={`row-${rowName}`}>
          <div className="subcat-row-title">
            <span className="subcat-row-name">{rowName}</span>
            <span className="subcat-row-move">
              <button type="button" className="subcat-row-move-btn" title="Поднять рядок выше" aria-label="Поднять рядок" disabled={ri === 0} onClick={() => onMoveSubcategoryRow && onMoveSubcategoryRow(activeSubcategory || '', rowName, 'up')}>▲</button>
              <button type="button" className="subcat-row-move-btn" title="Опустить рядок ниже" aria-label="Опустить рядок" disabled={ri === arr.length - 1} onClick={() => onMoveSubcategoryRow && onMoveSubcategoryRow(activeSubcategory || '', rowName, 'down')}>▼</button>
              <button type="button" className="subcat-row-rename-btn" title="Переименовать рядок" aria-label="Переименовать рядок" onClick={() => onOpenRenameRowModal && onOpenRenameRowModal(activeSubcategory || '', rowName)}></button>
            </span>
          </div>
          <div className="subcat-row-track">
            {subcategories.map((s, i) => ({ s, i })).filter((it) => activeSubcategoryRowsMeta.rowOf[it.s] === rowName).map((it) => renderSubcatChip(it.s, it.i))}
            {words.map((word, index) => ({ word, index })).filter((it) => it.word.row === rowName).map((it) => renderWordChip(it.word, it.index))}
          </div>
        </div>
      ))}

      <div className="word-grid">
        {currentNodeHasSubcategories && subcategories.map((s, i) => ({ s, i })).filter((it) => !activeSubcategoryRowsMeta.rows.includes(activeSubcategoryRowsMeta.rowOf[it.s])).map((it) => renderSubcatChip(it.s, it.i))}

        {words.map((word, index) => ({ word, index })).filter((it) => !(it.word.row && activeSubcategoryRowsMeta.rows.includes(it.word.row))).map((it) => renderWordChip(it.word, it.index))}

        {shouldShowWordAdd && (
          <div className={`quick-add ${isPhraseCategory ? 'phrase-quick-add' : ''}`}>
            <input
              value={newWord}
              onChange={(event) => onNewWordChange(event.target.value)}
              placeholder={isPhraseCategory ? 'Добавить фразу...' : 'Добавить слово...'}
              maxLength={wordInputMaxLength}
            />
            <label className="capital-checkbox" title="Показывать с большой буквы">
              <input
                type="checkbox"
                checked={newWordCapitalized}
                onChange={(event) => onNewWordCapitalizedChange(event.target.checked)}
              />
              <span>A</span>
            </label>
            <button type="button" onClick={onAddNode} aria-label="Добавить">
              +
            </button>
          </div>
        )}

      </div>

      {usagePanel && usagePanel.usages && usagePanel.usages.length > 0 && (
        <section
          className="word-usage-panel"
          onClick={() => setUsagePanel((p) => p ? { ...p, index: (p.index + 1) % p.usages.length } : p)}
        >
          <header>
            <strong>{usagePanel.usages[usagePanel.index].word}</strong>
            <span>{usagePanel.index + 1} / {usagePanel.usages.length}</span>
            <button type="button" onClick={(e) => { e.stopPropagation(); setUsagePanel(null); }} aria-label="Закрыть примеры">
              x
            </button>
          </header>
          <div className="word-usage-list">
            <p>{usagePanel.usages[usagePanel.index].sentence}</p>
          </div>
          <p className="word-usage-hint">Нажми, чтобы показать следующее предложение</p>
        </section>
      )}
      {wordMenu && (
        <div
          ref={wordMenuRef}
          className="word-context-menu"
          style={{ left: wordMenu.x, top: wordMenu.y, maxHeight: wordMenu.maxHeight }}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="word-context-header">
            <div className="word-context-title">
              {wordMenu.word.link
                ? <a href={wordMenu.word.link} target="_blank" rel="noopener noreferrer" className="word-context-title-link" title={wordMenu.word.link}>{wordMenu.word.text}</a>
                : wordMenu.word.text}
              {wordMenu.word.story && (
                <button
                  type="button"
                  className="word-story-open-btn"
                  title="Открыть историю в анализаторе"
                  aria-label="Открыть историю в анализаторе"
                  onClick={() => { const s = wordMenu.word.story; setWordMenu(null); onOpenStoryInAnalyzer && onOpenStoryInAnalyzer(s); }}
                >
                  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                  </svg>
                </button>
              )}
              <button
                type="button"
                className="word-context-speak-btn"
                title="Прослушать слово"
                aria-label="Прослушать слово"
                onClick={(e) => { e.stopPropagation(); playMenuWordAudio(); }}
              >
                🔊
              </button>
            </div>
            <button
              type="button"
              className="word-context-close"
              onClick={() => setWordMenu(null)}
              aria-label="Закрыть"
            >
              ×
            </button>
          </div>

          {(() => {
            const w = wordMenu.word;
            const active = getActiveLevels(w);
            const current = LEVEL_ORDER.includes(w.lvl) ? w.lvl : 'A1';
            if (active.length === 0) {
              return (
                <div className="word-level-action">
                  <span>Уровень</span>
                  <span className="word-level-neutral">нейтрал</span>
                </div>
              );
            }
            const filled = new Set(active);
            filled.add(current);
            let maxIdx = 0;
            LEVEL_ORDER.forEach((lv, i) => { if (filled.has(lv)) maxIdx = Math.max(maxIdx, i); });
            const options = LEVEL_ORDER.slice(0, maxIdx + 1);
            const nextLevel = LEVEL_ORDER[maxIdx + 1] || null;
            const curSlot = getWordLevels(wordMenu.word)[current];
            const isShared = !!(curSlot && curSlot.shared);
            return (
              <div className="word-level-action">
                <span>Уровень</span>
                <select value={current} onChange={(event) => changeWordLevel(event.target.value)}>
                  {options.map((lv) => (<option key={lv} value={lv}>{lv}</option>))}
                </select>
                <button
                  type="button"
                  className={'word-level-share' + (isShared ? ' is-shared' : '')}
                  title={isShared ? 'Эта пара отмечена как общая — ею делятся со всеми. Нажмите, чтобы снять' : 'Поделиться этой парой со всеми пользователями'}
                  aria-label="Поделиться парой"
                  onClick={toggleShareLevel}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                </button>
                {nextLevel && (
                  <button type="button" className="word-level-add" title="Добавить уровень" aria-label="Добавить уровень" onClick={() => changeWordLevel(nextLevel)}>＋</button>
                )}
              </div>
            );
          })()}

          {onOpenOrdskjonnhet && (
            <button
              type="button"
              className="word-ord-open-btn"
              onClick={() => { const idx = wordMenu.index; setWordMenu(null); onOpenOrdskjonnhet(idx); }}
            >
              Offentlig
            </button>
          )}

          {(currentCategoryType ? currentCategoryType === 'noun' : wordMenu.word.type === 'noun') && (
            <div className="word-gender-action">
              <span>Род</span>
              <div className="word-gender-buttons">
                {['en', 'ei', 'et'].map((g) => (
                  <button
                    type="button"
                    key={g}
                    data-g={g}
                    className={`word-gender-btn ${wordMenu.word.gender === g ? 'is-active' : ''}`}
                    onClick={() => applyWordGender(wordMenu.word.gender === g ? null : g)}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>
          )}

          {(() => {
            const ordPhoto = (wordMenu.word.ord && wordMenu.word.ord.photo && wordMenu.word.ord.photo.dataUrl) ? wordMenu.word.ord.photo : null;
            const menuPhoto = (wordMenu.word.photo && wordMenu.word.photo.dataUrl) ? wordMenu.word.photo : (ordPhoto || publicThumbFor(wordMenu.word));
            if (!menuPhoto || !menuPhoto.dataUrl) return null;
            const savedPos = menuPhoto.position ?? 50;
            const previewPos = draftPhotoPosition ?? savedPos;
            const hasUnsavedChanges = draftPhotoPosition !== null && draftPhotoPosition !== savedPos;
            const curLvl = LEVEL_ORDER.includes(wordMenu.word.lvl) ? wordMenu.word.lvl : 'A1';
            const ownSlot = getWordLevels(wordMenu.word)[curLvl];
            const hasOwnPhoto = !!(ownSlot && ownSlot.photo);
            const isOrdPhoto = !(wordMenu.word.photo && wordMenu.word.photo.dataUrl);
            return (
              <div className="word-context-photo">
                <img src={menuPhoto.dataUrl} alt={menuPhoto.name || wordMenu.word.text} onClick={(e) => { e.stopPropagation(); setLightboxPhoto(menuPhoto); }} style={{cursor: 'zoom-in', objectPosition: '50% ' + previewPos + '%'}} />
                {(menuPhoto && menuPhoto.dataUrl) ? (
                  <div className="photo-pos-control">
                    <label>Положение по вертикали</label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="5"
                      value={previewPos}
                      onChange={(e) => setDraftPhotoPosition(Number(e.target.value))}
                    />
                    <div className="photo-pos-hints"><span>верх</span><span>низ</span></div>
                    {hasUnsavedChanges && (
                      <button
                        type="button"
                        className="photo-pos-save"
                        onClick={() => { updatePhotoPosition(draftPhotoPosition); setDraftPhotoPosition(null); }}
                      >Сохранить</button>
                    )}
                  </div>
                ) : (
                  <div className="word-photo-inherited-note">{isOrdPhoto ? 'Фото добавлено в карточке слова (сс)' : 'Фото унаследовано с нижнего уровня'}</div>
                )}
                <button type="button" onClick={deletePhoto} disabled={!hasOwnPhoto} title={hasOwnPhoto ? 'Удалить своё фото уровня' : 'На этом уровне нет своего фото'}>
                  Удалить фото
                </button>
              </div>
            );
          })()}

          {(wordMenu.word.translations || []).length > 0 && (
            <div className="word-context-translations">
              {(wordMenu.word.translations || []).map((translation, index) => (
                <span className="translation-chip" key={`${translation}-${index}`}>
                  <span
                    className="translation-text"
                    onMouseEnter={(event) => showTranslationTooltip(event, translation)}
                    onMouseLeave={() => setTranslationTooltip(null)}
                  >
                    {translation}
                  </span>
                  <button
                    type="button"
                    onClick={() => deleteTranslation(index)}
                    aria-label={`Удалить перевод ${translation}`}
                  >
                    x
                  </button>
                </span>
              ))}
            </div>
          )}

          {(wordMenu.word.synonyms || []).length > 0 && (
            <div className="word-context-tags">
              {(wordMenu.word.synonyms || []).map((synonym, index) => (
                <span className="relation-chip synonym-chip" key={`${synonym}-${index}`}>
                  {synonym}
                  <button
                    type="button"
                    onClick={() => deleteRelation('synonyms', index)}
                    aria-label={`Удалить синоним ${synonym}`}
                  >
                    x
                  </button>
                </span>
              ))}
            </div>
          )}

          {(wordMenu.word.antonyms || []).length > 0 && (
            <div className="word-context-tags">
              {(wordMenu.word.antonyms || []).map((antonym, index) => (
                <span className="relation-chip antonym-chip" key={`${antonym}-${index}`}>
                  {antonym}
                  <button
                    type="button"
                    onClick={() => deleteRelation('antonyms', index)}
                    aria-label={`Удалить антоним ${antonym}`}
                  >
                    x
                  </button>
                </span>
              ))}
            </div>
          )}

          {(wordMenu.word.adjectives || []).length > 0 && (
            <div className="word-context-tags">
              {(wordMenu.word.adjectives || []).map((adjective, index) => (
                <span className="relation-chip adjective-chip" key={`${adjective}-${index}`}>
                  {adjective}
                  <button
                    type="button"
                    onClick={() => deleteRelation('adjectives', index)}
                    aria-label={`Удалить прилагательное ${adjective}`}
                  >
                    x
                  </button>
                </span>
              ))}
            </div>
          )}

          {getWordRoots(wordMenu.word).length > 0 && (
            <div className="word-context-tags">
              {getWordRoots(wordMenu.word).map((item, index) => (
                <span className="relation-chip root-display-chip" key={`rootdisp-${item.root}-${index}`}>
                  {item.root}{item.translation ? ` — ${item.translation}` : ''}
                  <button
                    type="button"
                    onClick={() => deleteRoot(item.root)}
                    aria-label={`Удалить корень ${item.root}`}
                  >
                    x
                  </button>
                </span>
              ))}
            </div>
          )}

          <button
            className="word-context-action"
            type="button"
            onClick={() => { if (!wordMenu) return; const idx = wordMenu.index; const cur = wordMenu.word.text || ''; setWordMenu(null); onOpenEditWordModal && onOpenEditWordModal(idx, cur); }}
          >
            Изменить
          </button>

          {(() => {
            const otherCategories = (categoryOrder || []).filter((c) => c !== activeCategory && db && db[c]);
            const hasLocal = menuTargetSubcategories.length > 0;
            const hasGlobal = otherCategories.length > 0;
            if (!hasLocal && !hasGlobal) {
              return (
                <div className="word-context-empty">Нет других разделов</div>
              );
            }
            return (
              <>
                {['move','copy'].map((modeKey) => (
                  <Fragment key={modeKey}>
                    <button
                      className="word-context-action transfer-toggle"
                      type="button"
                      onClick={() => { setActiveTransferMode((m) => (m === modeKey ? null : modeKey)); setTransferScope(null); }}
                    >
                      <span>{modeKey === 'move' ? 'Перенести' : 'Дублировать'}</span>
                      <span className="context-arrow">›</span>
                    </button>
                    {activeTransferMode === modeKey && (
                      <div className="word-context-inline-list">
                        {hasLocal && (
                          <button type="button" onClick={() => setTransferScope(transferScope === 'local' ? null : 'local')}>
                            Локально {transferScope === 'local' ? '▾' : '▸'}
                          </button>
                        )}
                        {transferScope === 'local' && menuTargetSubcategories.map((sub) => (
                          <button
                            key={sub}
                            type="button"
                            title={getPathLabel(sub)}
                            onClick={() => applyMenuAction(sub, modeKey)}
                            style={{ paddingLeft: 18 }}
                          >
                            {getPathLabel(sub)}
                          </button>
                        ))}
                        {hasGlobal && (
                          <button type="button" onClick={() => setTransferScope(transferScope === 'global' ? null : 'global')}>
                            Глобально {transferScope === 'global' ? '▾' : '▸'}
                          </button>
                        )}
                        {transferScope === 'global' && otherCategories.map((cat) => (
                          <button
                            key={cat}
                            type="button"
                            title={cat}
                            onClick={() => applyMenuAction(null, modeKey, cat)}
                            style={{ paddingLeft: 18 }}
                          >
                            {cat}
                          </button>
                        ))}
                        {modeKey === 'move' && activeSubcategoryRowsMeta.rows.length > 0 && (
                          <button type="button" onClick={() => setTransferScope(transferScope === 'row' ? null : 'row')}>
                            В рядок {transferScope === 'row' ? '▾' : '▸'}
                          </button>
                        )}
                        {modeKey === 'move' && transferScope === 'row' && activeSubcategoryRowsMeta.rows.map((rowName) => (
                          <button key={`wordrow-${rowName}`} type="button" style={{ paddingLeft: 18 }} onClick={() => { if (onSetWordRow) onSetWordRow(wordMenu.index, rowName); setWordMenu(null); }}>
                            {rowName}{wordMenu.word.row === rowName ? ' \u2713' : ''}
                          </button>
                        ))}
                        {modeKey === 'move' && transferScope === 'row' && wordMenu.word.row && (
                          <button type="button" style={{ paddingLeft: 18 }} onClick={() => { if (onSetWordRow) onSetWordRow(wordMenu.index, null); setWordMenu(null); }}>
                            За рядок (убрать)
                          </button>
                        )}
                      </div>
                    )}
                  </Fragment>
                ))}
              </>
            );
          })()}

          <div className="word-context-divider" />

          <button
            className={'word-context-action add-toggle' + (isAddMenuOpen ? ' is-open' : '')}
            type="button"
            onClick={() => setIsAddMenuOpen((open) => !open)}
          >
            Добавить {isAddMenuOpen ? '▲' : '▼'}
          </button>

          {isAddMenuOpen && (<>

          {!isAddingTranslation && (
            <button
              className="word-context-action"
              type="button"
              disabled={(wordMenu.word.translations || []).length >= 5}
              onClick={() => {
                setActiveRelationForm(null);
                setRelationDraft('');
                setIsEditingForms(false);
                setFormsDraft({});
                setIsEditingRoot(false);
                setRootDraft('');
                setRootTranslationDraft('');
                setIsAddingTranslation(true);
              }}
            >
              {(wordMenu.word.translations || []).length >= 5
                ? 'Добавлено 5 переводов'
                : 'Добавить перевод'}
            </button>
          )}

          <button
            className="word-context-action"
            type="button"
            disabled={(wordMenu.word.synonyms || []).length >= 3}
            onClick={() => showRelationForm('synonyms')}
          >
            {(wordMenu.word.synonyms || []).length >= 3
              ? 'Добавлено 3 синонима'
              : 'Добавить синоним'}
          </button>

          <button
            className="word-context-action"
            type="button"
            disabled={(wordMenu.word.antonyms || []).length >= 3}
            onClick={() => showRelationForm('antonyms')}
          >
            {(wordMenu.word.antonyms || []).length >= 3
              ? 'Добавлено 3 антонима'
              : 'Добавить антоним'}
          </button>

          {isNounWord && (
            <button
              className="word-context-action"
              type="button"
              disabled={(wordMenu.word.adjectives || []).length >= 3}
              onClick={() => showRelationForm('adjectives')}
            >
              {(wordMenu.word.adjectives || []).length >= 3
                ? 'Добавлено 3 прилагательных'
                : 'Добавить прилагательное'}
            </button>
          )}

          {canEditForms && (
            <button
              className="word-context-action"
              type="button"
              onClick={showFormsForm}
            >
              {wordMenu.word.manualForms || wordMenu.word.forms ? 'Изменить формы вручную' : 'Добавить формы вручную'}
            </button>
          )}

          <button
            className="word-context-action"
            type="button"
            onClick={() => { if (!wordMenu) return; const idx = wordMenu.index; const cur = wordMenu.word.link || ''; setWordMenu(null); onOpenWordLinkModal && onOpenWordLinkModal(idx, cur); }}
          >
            {wordMenu.word.link ? 'Изменить ссылку (YouTube/TikTok)' : 'Добавить ссылку (YouTube/TikTok)'}
          </button>

          <button
            className="word-context-action"
            type="button"
            onClick={() => { if (!wordMenu) return; const idx = wordMenu.index; const cur = wordMenu.word.story || ''; setWordMenu(null); onOpenWordStoryModal && onOpenWordStoryModal(idx, cur); }}
          >
            {wordMenu.word.story ? 'Изменить описание (историю)' : 'Добавить описание (историю)'}
          </button>

          <button
            className="word-context-action"
            type="button"
            onClick={showRootForm}
          >
            {getWordRoots(wordMenu.word).length > 0
              ? `Корни: ${getWordRoots(wordMenu.word).map((item) => item.root).join(', ')}`
              : 'Добавить корень'}
          </button>

          <label className="word-context-action file-action">
            {wordMenu.word.photo ? 'Заменить фото' : 'Добавить фото'}
            <input
              type="file"
              accept="image/jpeg,image/png,image/heic,image/heif,.jpg,.jpeg,.png,.heic,.heif"
              onChange={addPhoto}
            />
          </label>

          <button
            className="word-context-action"
            type="button"
            onClick={showPhraseForm}
          >
            {wordMenu.word.phrase ? 'Изменить фразу' : 'Добавить фразу'}
          </button>

          </>)}

          <div className="word-context-divider" />

          <div className="word-text-matches">
            <div className="word-text-matches-title">Тексты</div>
            {(() => {
              const cards = getWordTexts ? getWordTexts(wordMenu.word) : [];
              if (cards.length === 0) {
                return <div className="word-context-empty">Нет упоминаний</div>;
              }
              const usagesFlat = [];
              const offsets = [];
              cards.forEach((card) => {
                offsets.push(usagesFlat.length);
                card.sentences.forEach((s) => usagesFlat.push({ word: card.text, sentence: s }));
              });
              return (
                <div className="word-text-story-list">
                  {cards.map((card, i) => (
                    <button
                      type="button"
                      className="word-text-story-item"
                      key={card.text}
                      onClick={() => openUsage(usagesFlat, offsets[i])}
                    >
                      <span className="word-text-story-word">{card.text}</span>
                      <span className="word-text-story-num">{i + 1}</span>
                    </button>
                  ))}
                </div>
              );
            })()}
          </div>

          {isAddingTranslation && (
            <form className="translation-form" onSubmit={submitTranslation}>
              <input
                value={translationDraft}
                onChange={(event) => setTranslationDraft(event.target.value)}
                placeholder={isPhraseCategory ? 'Перевод или пояснение...' : 'Перевод...'}
                autoFocus
                maxLength={translationMaxLength}
              />
              <button type="submit">+</button>
            </form>
          )}

          {isAddingPhrase && (
            <form className="translation-form phrase-form" onSubmit={submitPhrase}>
              <input
                value={phraseDraft}
                onChange={(event) => setPhraseDraft(event.target.value.slice(0, 200))}
                placeholder="Фраза с этим словом (до 200 символов)..."
                autoFocus
                maxLength={200}
              />
              <button type="submit">OK</button>
            </form>
          )}

          {activeRelationForm && (
            <form className="translation-form" onSubmit={submitRelation}>
              <input
                value={relationDraft}
                onChange={(event) => setRelationDraft(event.target.value)}
                placeholder={
                  activeRelationForm === 'synonyms'
                    ? 'Синоним...'
                    : activeRelationForm === 'adjectives'
                      ? 'Прилагательное...'
                      : 'Антоним...'
                }
                autoFocus
                maxLength={40}
              />
              <button type="submit">+</button>
            </form>
          )}

          {isEditingRoot && (
            <>
              {getWordRoots(wordMenu.word).length > 0 && (
                <div className="word-context-tags root-chip-list">
                  {getWordRoots(wordMenu.word).map((item) => (
                    <span className="relation-chip root-chip" key={item.root}>
                      <span className="root-chip-text">{item.root}</span>
                      {item.translation && (
                        <small
                          onMouseEnter={(event) => showTranslationTooltip(event, item.translation)}
                          onMouseLeave={() => setTranslationTooltip(null)}
                        >
                          {item.translation}
                        </small>
                      )}
                      <button
                        type="button"
                        onClick={() => deleteRoot(item.root)}
                        aria-label={`Удалить корень ${item.root}`}
                      >
                        x
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <form className="translation-form" onSubmit={submitRoot}>
                <input
                  value={rootDraft}
                  onChange={(event) => setRootDraft(event.target.value)}
                  placeholder="Корень..."
                  autoFocus
                  maxLength={32}
                />
                <input
                  value={rootTranslationDraft}
                  onChange={(event) => setRootTranslationDraft(event.target.value)}
                  placeholder="Перевод корня..."
                  maxLength={80}
                />
                <button type="submit">+</button>
              </form>
            </>
          )}

          {isEditingForms && (
            <form className="forms-edit-form" onSubmit={submitForms}>
              {isNounWord && (
                <>
                  <label>
                    <span>best. ent.</span>
                    <input
                      value={formsDraft.singularDefinite || ''}
                      onChange={(event) => updateFormsDraft('singularDefinite', event.target.value)}
                      placeholder="koppen"
                      autoFocus
                    />
                  </label>
                  <label>
                    <span>ubest. fl.</span>
                    <input
                      value={formsDraft.pluralIndefinite || ''}
                      onChange={(event) => updateFormsDraft('pluralIndefinite', event.target.value)}
                      placeholder="kopper"
                    />
                  </label>
                  <label>
                    <span>best. fl.</span>
                    <input
                      value={formsDraft.pluralDefinite || ''}
                      onChange={(event) => updateFormsDraft('pluralDefinite', event.target.value)}
                      placeholder="koppene"
                    />
                  </label>
                </>
              )}

              {isVerbWord && (
                <>
                  <label>
                    <span>Infinitiv</span>
                    <input
                      value={formsDraft.infinitive || wordMenu.word.text || ''}
                      onChange={(event) => updateFormsDraft('infinitive', event.target.value)}
                      placeholder="inneholde"
                      autoFocus
                    />
                  </label>
                  <label>
                    <span>Presens</span>
                    <input
                      value={formsDraft.present || ''}
                      onChange={(event) => updateFormsDraft('present', event.target.value)}
                      placeholder="inneholder"
                    />
                  </label>
                  <label>
                    <span>Preteritum</span>
                    <input
                      value={formsDraft.past || ''}
                      onChange={(event) => updateFormsDraft('past', event.target.value)}
                      placeholder="inneholdt"
                    />
                  </label>
                  <label>
                    <span>Perfektum</span>
                    <input
                      value={formsDraft.perfect || ''}
                      onChange={(event) => updateFormsDraft('perfect', event.target.value)}
                      placeholder="inneholdt"
                    />
                  </label>
                  <label>
                    <span>Imperativ</span>
                    <input
                      value={formsDraft.imperative || ''}
                      onChange={(event) => updateFormsDraft('imperative', event.target.value)}
                      placeholder="innehold"
                    />
                  </label>
                </>
              )}
              {isAdjectiveWord && (
                <>
                  <label>
                    <span>positiv</span>
                    <input
                      value={formsDraft.positive || wordMenu.word.text || ''}
                      onChange={(event) => updateFormsDraft('positive', event.target.value)}
                      placeholder="stor"
                      autoFocus
                    />
                  </label>
                  <label>
                    <span>nøytr.</span>
                    <input
                      value={formsDraft.neuter || ''}
                      onChange={(event) => updateFormsDraft('neuter', event.target.value)}
                      placeholder="stort"
                    />
                  </label>
                  <label>
                    <span>fl./best.</span>
                    <input
                      value={formsDraft.plural || formsDraft.definite || ''}
                      onChange={(event) => {
                        updateFormsDraft('plural', event.target.value);
                        updateFormsDraft('definite', event.target.value);
                      }}
                      placeholder="store"
                    />
                  </label>
                  <label>
                    <span>komp.</span>
                    <input
                      value={formsDraft.comparative || ''}
                      onChange={(event) => updateFormsDraft('comparative', event.target.value)}
                      placeholder="større"
                    />
                  </label>
                  <label>
                    <span>sup. ubest.</span>
                    <input
                      value={formsDraft.superlativeIndefinite || ''}
                      onChange={(event) => updateFormsDraft('superlativeIndefinite', event.target.value)}
                      placeholder="størst"
                    />
                  </label>
                  <label>
                    <span>sup. best.</span>
                    <input
                      value={formsDraft.superlativeDefinite || ''}
                      onChange={(event) => updateFormsDraft('superlativeDefinite', event.target.value)}
                      placeholder="største"
                    />
                  </label>
                </>
              )}
              {isAdverbWord && (
                <>
                  <label>
                    <span>positiv</span>
                    <input
                      value={formsDraft.positive || wordMenu.word.text || ''}
                      onChange={(event) => updateFormsDraft('positive', event.target.value)}
                      placeholder="fort"
                      autoFocus
                    />
                  </label>
                  <label>
                    <span>komp.</span>
                    <input
                      value={formsDraft.comparative || ''}
                      onChange={(event) => updateFormsDraft('comparative', event.target.value)}
                      placeholder="fortere"
                    />
                  </label>
                  <label>
                    <span>sup.</span>
                    <input
                      value={formsDraft.superlative || ''}
                      onChange={(event) => updateFormsDraft('superlative', event.target.value)}
                      placeholder="fortest"
                    />
                  </label>
                </>
              )}

              <button type="submit">OK</button>
            </form>
          )}

          <div className="word-context-divider" />
          <button
            type="button"
            className="word-context-save-action"
            onClick={() => setWordMenu(null)}
          >
            Сохранить
          </button>
          <button
            type="button"
            className="word-context-delete-action"
            onClick={deleteWordFromMenu}
          >
            Удалить
          </button>
        </div>
      )}

      {subcategoryMenu && (
        <div
          ref={subcategoryMenuRef}
          className="word-context-menu subcategory-context-menu"
          style={{ left: subcategoryMenu.x, top: subcategoryMenu.y, maxHeight: subcategoryMenu.maxHeight }}
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            className="word-context-close"
            onClick={() => setSubcategoryMenu(null)}
            aria-label="Закрыть"
          >
            ×
          </button>
          <div className="word-context-title">{subcategoryMenu.name}</div>

          {onPinSubcategoryToConstructor && (
            <button
              className="word-context-action"
              type="button"
              onClick={pinSubcategoryToConstructor}
            >
              Прикрепить на панель
            </button>
          )}

          <form className="subcategory-rename-form" onSubmit={submitSubcategoryRename}>
            <input
              value={subcategoryNameDraft}
              onChange={(event) => setSubcategoryNameDraft(event.target.value)}
              autoFocus
            />
            <button type="submit">OK</button>
          </form>

          {getSectionTargets().length > 0 && (
            <>
              {['copy','move'].map((modeKey) => {
                const label = modeKey === 'copy' ? 'Дублировать раздел' : 'Перенести раздел';
                const localTargets = getSectionTargets('local');
                const globalTargets = getSectionTargets('global');
                const hasLocal = localTargets.length > 0;
                const hasGlobal = globalTargets.length > 0;
                if (!hasLocal && !hasGlobal) return null;
                return (
                  <Fragment key={modeKey}>
                    <button
                      className="word-context-action transfer-toggle"
                      type="button"
                      onClick={() => { setActiveSectionTransferMode((m) => (m === modeKey ? null : modeKey)); setTransferScope(null); }}
                    >
                      <span>{label}</span>
                      <span className="context-arrow">›</span>
                    </button>
                    {activeSectionTransferMode === modeKey && (
                      <div className="word-context-inline-list">
                        {hasLocal && (
                          <button type="button" onClick={() => setTransferScope(transferScope === 'local' ? null : 'local')}>
                            Локально {transferScope === 'local' ? '▾' : '▸'}
                          </button>
                        )}
                        {transferScope === 'local' && localTargets.map((target) => (
                          <button
                            key={`${target.category}-${target.path}`}
                            type="button"
                            title={target.label}
                            onClick={() => applySectionAction(target, modeKey)}
                            style={{ paddingLeft: 18 }}
                          >
                            {target.label}
                          </button>
                        ))}
                        {hasGlobal && (
                          <button type="button" onClick={() => setTransferScope(transferScope === 'global' ? null : 'global')}>
                            Глобально {transferScope === 'global' ? '▾' : '▸'}
                          </button>
                        )}
                        {transferScope === 'global' && globalTargets.map((target) => (
                          <button
                            key={`${target.category}-${target.path}`}
                            type="button"
                            title={target.label}
                            onClick={() => applySectionAction(target, modeKey)}
                            style={{ paddingLeft: 18 }}
                          >
                            {target.label}
                          </button>
                        ))}
                        {modeKey === 'move' && activeSubcategoryRowsMeta.rows.length > 0 && (
                          <>
                            <button type="button" onClick={() => setTransferScope(transferScope === 'row' ? null : 'row')}>
                              В рядок {transferScope === 'row' ? '▾' : '▸'}
                            </button>
                            {transferScope === 'row' && activeSubcategoryRowsMeta.rows.map((rowName) => (
                              <button key={`assignrow-${rowName}`} type="button" style={{ paddingLeft: 18 }} onClick={() => { onAssignSubcategoryRow && onAssignSubcategoryRow(subcategoryMenu.name, rowName); setSubcategoryMenu(null); }}>
                                {rowName}{activeSubcategoryRowsMeta.rowOf[subcategoryMenu.name] === rowName ? ' ✓' : ''}
                              </button>
                            ))}
                            {activeSubcategoryRowsMeta.rowOf[subcategoryMenu.name] && (
                              <button type="button" style={{ paddingLeft: 18 }} onClick={() => { onAssignSubcategoryRow && onAssignSubcategoryRow(subcategoryMenu.name, null); setSubcategoryMenu(null); }}>
                                За рядок
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </Fragment>
                );
              })}
            </>
          )}

          <button
            className="word-context-action"
            type="button"
            onClick={makeSubcategoryDeck}
          >
            Сделать колодой
          </button>

          <button
            className="word-context-action danger-action"
            type="button"
            onClick={deleteSubcategory}
          >
            Удалить подкатегорию
          </button>
        </div>
      )}

      {deckConfirmPath && (
        <div className="deck-confirm-overlay" onClick={() => setDeckConfirmPath(null)}>
          <div className="deck-confirm-box" onClick={(e) => e.stopPropagation()}>
            <p className="deck-confirm-text">Добавить эту подкатегорию в изучаемые карточки отдельной колодой?</p>
            <div className="deck-confirm-actions">
              <button type="button" className="deck-confirm-yes" onClick={confirmMakeDeck}>Да</button>
              <button type="button" className="deck-confirm-no" onClick={() => setDeckConfirmPath(null)}>Нет</button>
            </div>
          </div>
        </div>
      )}
      {translationTooltip && (
        <div
          className="floating-translation-tooltip"
          style={{ left: translationTooltip.x, top: translationTooltip.y }}
        >
          {translationTooltip.text}
        </div>
      )}
      {lightboxPhoto && lightboxPhoto.dataUrl && (
        <div
          className="photo-lightbox-overlay"
          onClick={() => setLightboxPhoto(null)}
        >
          <img src={lightboxPhoto.dataUrl} alt={lightboxPhoto.name || ""} className="photo-lightbox-img" />
        </div>
      )}
      {photoAlert && (
        <div className="subcat-delete-overlay" onClick={() => setPhotoAlert(null)}>
          <div className="subcat-delete-card" onClick={(e) => e.stopPropagation()}>
            <p className="subcat-delete-title">Не удалось добавить фото</p>
            <p className="subcat-delete-sub">{photoAlert}</p>
            <div className="subcat-delete-actions">
              <button type="button" className="subcat-row-confirm" onClick={() => setPhotoAlert(null)}>Понятно</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
